// Prague, "Lamps on Charles Bridge". A mild June night on the Old Town embankment of the Vltava. Across the river
// Prague Castle sits on its hill, lit gold: the long palace front with St. Vitus Cathedral rising above it (the tall
// main tower with its green copper helmet, the twin Gothic west spires, the spiky choir). Below it the roofs of Mala
// Strana and the green dome of St. Nicholas, Petrin hill with its little lattice lookout tower, and the metronome
// ticking on Letna. Charles Bridge crosses the river from the Old Town Bridge Tower at our end to the Lesser Town
// towers: stone arches glowing from underneath, dark baroque statues on the parapet, warm lamps, people strolling,
// the arches mirrored in the river and the weir's broken line of foam in front. Swans and ducks paddle, a paddle
// steamer strung with bulbs glides past, a red-and-cream tram rattles along the embankment, a couple sits on a bench
// by the balustrade under an old lamp, linden branches frame the top and linden blossoms spin down at three depths.
// The city's billboard is a lit rooftop sign with a ring of chasing bulbs on a Mala Strana house below the castle.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, cycle, scroll,
  hash, rng, bayer, hex, makeCanvas, makeHalo, drawHalo, text, streak, makePitch, hands } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const HOR = 172;                                    // eye level (the far waterlines crowd up towards it)
// Charles Bridge in perspective: station s runs from 0 (the Old Town end, near, right) to 1 (the Lesser Town end,
// twice as far, left). Screen x, scale, waterline, deck and parapet all follow from s.
const XO = 405, XL = 120, NARCH = 16;
const bsc = s => 1 / (1 + s);
const bxAt = s => XO - (XO - XL) * 2 * s / (1 + s);
const sAtX = x => { const g = clamp((XO - x) / (XO - XL), 0, 1); return g / (2 - g); };
const bWater = s => HOR + 24 * bsc(s);              // 196 at the Old Town end, 184 at the far end
const bDeck = s => HOR - 6 * bsc(s);                // the deck cornice
const bRail = s => bDeck(s) - 3 * bsc(s);           // top of the parapet
const OT = { cx: 405, x0: 392, x1: 418, top: 118, base: 198 };   // the Old Town Bridge Tower (body)
const FWL = 179;                                    // far waterline downstream, beyond the bridge
const KWL = 184;                                    // waterline at Kampa (left of the bridge's far end)
const NL = { x0: 386, deck: 204, wl: 214 };         // the Novotneho lavka pier with its terrace (right)
const WEIR_X1 = 386;
const weirY = x => 197 + 15 * x / WEIR_X1;         // the weir's crest, from Kampa (left) to the pier (right)
const WB = 242;                                     // the near water runs on behind the balustrade down to here
const BAL = 229;                                    // top of the balustrade's coping
const SIDE = 242, KERB = 247, RAIL_F = 252, RAIL_N = 257, PAVE = 263;   // sidewalk, kerb, the two rails, our pavement
const TRAM = { w: 134, h: 45, y: 213 };             // sprite (pantograph included): roof at 225, wheels on RAIL_N
const WIRE_L = 213, WIRE_R = 212, WIRE_X1 = 440;   // the tram's contact wire (left end, bracket end), sagging a little
const wireY = x => Math.round(lerp(WIRE_L, WIRE_R, x / WIRE_X1) + 2.2 * Math.sin(Math.PI * clamp(x / WIRE_X1, 0, 1)));
const LP = { x: 452, y: 157, base: 268 };           // the old lamp post (lantern centre), also carrying the wire
const BENCH = { x: 86, w: 36 };
const MOON = { x: 344, y: 36 };
const PAL = { x0: 168, x1: 292, roof: 90, top: 95, base: 116 };  // the castle's long south front
const MT = { cx: 235, x0: 228, x1: 242 };           // St. Vitus main tower
const SPIRES = [204, 212];                          // the twin west spires (centre x)
const SN = { cx: 148 };                             // St. Nicholas (dome centre x)
const PETRIN = { x: 50, top: 93 };                  // the lookout tower on Petrin hill
const METRO = { x: 358, y: 131 };                   // the metronome's pivot on Letna
const BB = { x: 274, y: 128, w: 46, h: 20 };        // face of the rooftop sign (the city's billboard)
const BB_ROOF = 153;                                // the roof its iron legs stand on

/* ================= Timed events (loop seconds) ================= */
const BELLS = [42, 45.6, 49.2, 162, 165.6, 169.2];  // St. Vitus strikes
const TRAMS = [{ a: 14, b: 26, dir: -1, seed: 1 }, { a: 188, b: 200, dir: 1, seed: 2 }];
const STEAMER = { t0: 70, t1: 150 };                // the paddle steamer glides right to left
const LEAN = [[54, 98], [148, 206]];                // she rests her head on his shoulder
const WALKERS_FG = [                                // people on the riverside pavement in front of the balustrade
  { a: 33, b: 61, dir: 1, coat: '#6a4a3a', dog: true },
  { a: 118, b: 140, dir: -1, coat: '#3a4a62', dog: false },
  { a: 214, b: 236, dir: 1, coat: '#7a3a44', dog: false },
];
const PLANE = { t0: 126, t1: 206 };                  // a plane crossing high over the city
const SPARKS = 4800;                                // steps per loop the pantograph may spark on

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const mixHex = (a, b, t) => { const A = C(a), B = C(b); return toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))); };
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    get(i, j) { const o = (j * w + i) * 4; return [d[o], d[o + 1], d[o + 2]]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    clear(i, j) { if (i >= 0 && j >= 0 && i < w && j < h) d[(j * w + i) * 4 + 3] = 0; },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); optional period px in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const box = (x, y, w, h, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), w, h); };
const flipC = src => { const [c, x] = makeCanvas(src.width, src.height); x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0); return c; };
/** a straight 1 px line into a painter (build time) */
function line(p, x0, y0, x1, y1, col) {
  const n = Math.max(1, Math.round(Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0))));
  for (let k = 0; k <= n; k++) p.set(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), typeof col === 'function' ? col(k / n) : col);
}

/* ================= Palettes ================= */
const SKY = ['#050817', '#070b1d', '#090e23', '#0b1229', '#0e1530', '#111937', '#141d3e', '#182145', '#1c264b',
  '#212b51', '#272f56', '#2e345a', '#363a5d', '#3f3f5f', '#48445f', '#524a5e'];
const TEAL = [30, 74, 82];                          // the June twilight that lingers low in the north-west
const STONE = ['#1c1620', '#2a2026', '#3a2c2a', '#4e3a2e', '#664c34', '#806038', '#9c7840', '#b8924c', '#d2ac5e', '#e8c878', '#f8e4a4'];
const CREAM = ['#5a4432', '#7a5e40', '#9a7a4e', '#b8965c', '#d0ae6c', '#e2c47e', '#f0d898', '#faeabc'];
const SLATE = ['#12111a', '#1a1822', '#23202c', '#2e2a36', '#3a3542', '#4a4450'];
const CU = ['#0e2620', '#163a2e', '#20523e', '#2e6c50', '#428a64', '#62aa80', '#8ccaa0', '#bce8c8'];
const GOLD = ['#5a3a12', '#8a5e1a', '#b8862a', '#e0b040', '#f8d870', '#fff4c0'];
const ROOF = ['#1a0f10', '#261416', '#34191a', '#43201e', '#542822', '#683228'];
const WALLS = [['#2c2632', '#3a3240', '#4a404c', '#5c5058'], ['#30282a', '#403432', '#52423c', '#665244'],
  ['#2a2a2e', '#36363c', '#44444a', '#56545a'], ['#342a24', '#46382c', '#5a4834', '#70583e']];
const BRIDGE = ['#120e14', '#1a1418', '#231b1c', '#2e2320', '#3a2c24', '#4a382a', '#5e4630', '#765834', '#906c3c', '#aa8246'];
const LEAF = ['#060c09', '#09130d', '#0d1a11', '#122215', '#182c1a', '#1f371f', '#284424', '#34522a'];
const HILL = ['#05090d', '#070d10', '#0a1213', '#0d1716', '#111d1a', '#16241f', '#1c2b24', '#233428'];
const LEAFLIT = ['#2a3c1c', '#3e5226', '#566a30', '#72843a', '#909e46'];
const BLOSSOM = ['#b8a860', '#d8c878', '#efe29a', '#fff4c0'];
const BRACT = ['#7e9054', '#a0b270', '#c2d290', '#dde8b0'];
const WARMWIN = ['#fff0c4', '#ffd88a', '#f4b25e', '#d08a3e'];

/* ================= Sky, stars, moon, thin clouds ================= */
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const yy = Math.min(y, 186);
    let v = Math.pow(yy / 186, 1.3) * 0.76;
    v += Math.max(0, 1 - Math.hypot((x - MOON.x) / 120, (y - MOON.y) / 70)) * 0.16;      // moonlit haze
    v += Math.max(0, 1 - Math.hypot((x - 236) / 280, (yy - 190) / 80)) * 0.18;           // the city's glow, low down
    const col = C(pick(SKY, v, x, y));
    const f = 0.55 * smooth(90, 176, yy) * smooth(230, 470, x);                        // lingering June twilight (north-west)
    const q = Math.floor(f * 4 + bayer(x, y)) / 4;
    p.set(x, y, q > 0 ? col.map((c, i) => Math.round(lerp(c, TEAL[i], q * 0.55))) : col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(420);
  while (STARS.length < 64) {
    const x = Math.floor(r() * W), y = Math.floor(r() * 112);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 22) continue;
    if (x > 186 && x < 282 && y > 12) continue;                                    // keep the cathedral's sky clean
    if (x > 38 && x < 62 && y > 50) continue;                                      // ...and Petrin tower's
    const b = r();
    STARS.push({ x, y, k: 7 + Math.floor(r() * 33), ph: r(), b: 0.18 + b * b * 0.7, big: b > 0.94 });
  }
}
/** a waning gibbous moon, lit from the left */
function renderMoon() {
  const R = 7, s = 17, c0 = 8, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - c0, dy = y - c0, d = Math.hypot(dx, dy);
    if (d > R + 0.35) continue;
    const dark = dx > 0.5 * Math.sqrt(Math.max(0, R * R - dy * dy)) + 0.3;
    let col = dark ? '#1a2240' : '#f4eed8';
    if (!dark) {
      if (d > R - 0.8) col = '#dcd4b8';
      else if (dx > 0.5 * Math.sqrt(Math.max(0, R * R - dy * dy)) - 1) col = '#e2dac0';
      if ((dx + 2) ** 2 + (dy + 2) ** 2 < 2.2 || (dx - 1) ** 2 + (dy - 3) ** 2 < 1.2 || (dx + 4) ** 2 + (dy - 2) ** 2 < 0.8 || (dx - 2) ** 2 + (dy + 3) ** 2 < 0.8) col = '#d2caae';
    } else if (d > R - 0.8) col = '#222a48';
    p.set(i, y, col);
  }
  return p.done();
}
/** a tileable band of thin, separate cloud streaks (period W) */
function makeWisps(seed, h, n, pal) {
  const r = rng(seed), S = [];
  for (let i = 0; i < n; i++) S.push({ x: r() * W, y: h * (0.3 + r() * 0.4), len: 16 + r() * 34, th: 1.2 + r() * 1.6, tilt: (r() - 0.5) * 0.08, ph: r() * TAU });
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = 0;
    for (const q of S) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y - dx * q.tilt - Math.sin(dx / q.len * 2.6 + q.ph) * 1.3, v = 1 - (dx / q.len) ** 2 - (dy / q.th) ** 2;
      if (v > best) best = v;
    }
    D[y * W + x] = best * (0.7 + 0.6 * vnoise(x / 8, y / 1.5, seed, 60)) + (vnoise(x / 3, y, seed + 1, 160) - 0.5) * 0.22;
  }
  const get = (x, y) => (y < 0 || y >= h) ? 0 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = D[y * W + x], th = 0.14 + (bayer(x, y) - 0.5) * 0.08;
    if (v <= th - 0.08) continue;
    if (v <= th) { if (bayer(x + 1, y + 2) < 0.35) p.set(x, y, pal.body, 60); continue; }
    const col = get(x, y - 1) <= th ? pal.top : get(x, y + 1) <= th ? pal.bot : pal.body;
    p.set(x, y, col, v > th + 0.2 ? 160 : 105);
  }
  return p.done();
}

/* ================= The hills: Letna far right, Petrin with its lookout tower, the castle hill ================= */
const petY = x => 93 + (x <= PETRIN.x ? 0.0036 : 0.0042) * (x - PETRIN.x) ** 2;
/** the skyline of the hills behind Mala Strana (Petrin, Strahov, Hradcany, the castle plateau, the slope to Klarov) */
function ridgeY(x) {
  if (x <= 105) return petY(x);
  if (x <= 170) return lerp(petY(105), 114, (x - 105) / 65);
  if (x <= 300) return 116;
  return 116 + (x - 300) * 0.36;
}
const letnaY = x => 127 + (x - 290) * 0.07;
const HILL_LAMPS = [];                               // lanterns along the garden paths below the castle and up Petrin
function renderHills() {
  const p = pix(W, H), r = rng(77);
  // Letna, far behind on the right: a low dark ridge, bluish with distance, with the metronome's great plinth
  for (let x = 286; x < W; x++) for (let y = Math.floor(letnaY(x) - 1.5 * vnoise(x / 4, 0, 9)); y < 186; y++) {
    const v = 0.25 + 0.3 * vnoise(x / 3, y / 2, 11) + (y < letnaY(x) + 1 ? 0.3 : 0);
    p.set(x, y, pick(['#070c12', '#0a1117', '#0e161c', '#142024'], v, x, y));
  }
  for (let x = METRO.x - 14; x <= METRO.x + 12; x++) for (let y = METRO.y; y <= METRO.y + 3; y++)
    p.set(x, y, y === METRO.y ? '#5a5a6e' : x === METRO.x - 14 ? '#46465a' : '#2a2c3a');
  for (const lx of [322, 341, 380, 401]) p.set(lx, Math.round(letnaY(lx)) + 3, '#e8b868');
  // Petrin and the castle hill
  for (let x = 0; x < 400; x++) {
    const top = ridgeY(x) - (x < 108 ? 2.2 * vnoise(x / 2.6, 0, 3) : 0);
    for (let y = Math.floor(top); y < 190; y++) {
      const n = vnoise(x / 3.2, y / 2.6, 5), n2 = vnoise(x / 1.4, y / 1.4, 6);
      let col;
      if (x < 112) {                                                                   // Petrin's woods
        const lit = (y - top < 3 && n > 0.45) ? 0.5 : 0;
        col = pick(HILL, 0.1 + n * 0.4 + n2 * 0.2 + lit + 0.3 * smooth(125, 175, y), x, y);
        if (y - top < 2.2 && n2 > 0.55) col = '#1c2c2c';                             // moonlit crowns
      } else {                                                                         // gardens, walls, old houses
        col = pick(HILL, 0.14 + n * 0.35 + n2 * 0.18 + 0.28 * smooth(120, 170, y), x, y);
        if (y - top < 1.5) col = x < 300 ? '#22302a' : '#1a2826';
      }
      p.set(x, y, col);
    }
  }
  for (let k = 0; k < 14; k++) {                                                        // a lit path zigzagging up Petrin
    const u = k / 13, x = Math.round(lerp(128, 62, u) + Math.sin(u * 13) * 9), y = Math.round(lerp(150, 101, u));
    if (y < petY(x) + 3) continue;
    HILL_LAMPS.push({ x, y, k: 5 + Math.floor(r() * 20), ph: r() });
    p.set(x, y, '#f0c878');
  }
  // the Hunger Wall climbing Petrin: a crenellated line
  for (let x = 72; x <= 136; x++) {
    const y = Math.round(lerp(99, 134, (x - 72) / 64) + Math.sin(x * 0.2) * 0.8);
    p.set(x, y, '#4a4640'); p.set(x, y + 1, '#2a2826');
    if (x % 3 === 0) p.set(x, y - 1, '#5a564c');
  }
  // Strahov's twin towers on the ridge
  for (const tx of [98, 103]) {
    for (let y = 99; y < 106; y++) { p.set(tx, y, y < 101 ? '#1c3a30' : '#8a7e6a'); p.set(tx + 1, y, y < 101 ? '#12261e' : '#5a5046'); }
    p.set(tx, 98, '#2a5a44');
  }
  for (let x = 96; x <= 106; x++) for (let y = 104; y < 109; y++) p.set(x, y, y === 104 ? '#8a7a64' : (x % 2 ? '#5a4e44' : '#4a4038'));
  // Hradcany along the ridge: palaces and houses with a few lit windows
  for (let x = 108; x < 172; x++) {
    const top = Math.floor(ridgeY(x)) - 5 - (hash(x >> 3, 1, 4) < 0.5 ? 2 : 0);
    for (let y = top; y < ridgeY(x) + 4; y++) {
      let col = pick(['#2a2428', '#3a3032', '#4e4038', '#6a5842'], 0.3 + 0.4 * (y - top) / 9 + (x % 8 === 0 ? -0.2 : 0), x, y);
      if (y === top) col = '#3a2a26';
      if ((x % 3 === 1) && (y - top) % 3 === 1 && y > top + 1) col = hash(x, y, 12) < 0.3 ? '#f0c070' : '#1c1822';
      p.set(x, y, col);
    }
  }
  // the castle's south gardens: terraces with lamps along the paths
  for (let k = 0; k < 18; k++) {
    const x = 176 + Math.floor(r() * 150), y = 124 + Math.floor(r() * 12);
    if (x > 292 && y < ridgeY(x) + 3) continue;
    HILL_LAMPS.push({ x, y, k: 7 + Math.floor(r() * 20), ph: r() });
    p.set(x, y, '#ffd890');
  }
  return trim(p);
}
/** Petrin's lookout tower: a slim lattice of iron, lit gold (the beacon on top blinks per frame) */
function renderPetrinTower() {
  const p = pix(W, 110), cx = PETRIN.x, base = PETRIN.top, top = 64;
  const hw = y => lerp(4.5, 1.5, (base - y) / (base - top));
  for (let y = top; y <= base; y++) {
    const w = hw(y), xl = Math.round(cx - w), xr = Math.round(cx + w);
    p.set(xl, y, '#e8c070'); p.set(xr, y, '#a8783a');                                   // legs: lit left, shaded right
    const k = (base - y) % 7, span = xr - xl;
    if (span > 2) {                                                                      // X bracing between the legs
      const a = Math.round(xl + (k / 7) * span), b = Math.round(xr - (k / 7) * span);
      p.set(a, y, '#c89848'); p.set(b, y, '#9a6c34');
      if (k === 0) for (let i = xl; i <= xr; i++) p.set(i, y, '#d8aa58');
    }
    if (span <= 2) p.set(cx, y, '#c89040');
  }
  for (let i = cx - 4; i <= cx + 4; i++) { p.set(i, top + 3, '#f0cc80'); p.set(i, top + 4, '#8a6030'); }   // top gallery
  for (let i = cx - 3; i <= cx + 3; i++) p.set(i, top + 2, '#6a4a28');
  for (let y = top - 3; y < top + 2; y++) for (let i = cx - 1; i <= cx + 1; i++) p.set(i, y, i === cx - 1 ? '#f4d890' : '#c89848');   // lantern
  for (let y = top - 9; y < top - 3; y++) p.set(cx, y, '#8a7050');                    // mast
  for (let i = cx - 5; i <= cx + 5; i++) for (let y = base - 3; y <= base; y++)        // entrance pavilion at the foot
    p.set(i, y, y === base - 3 ? '#c8a060' : (i % 3 === 0 && y === base - 1) ? '#ffd890' : '#6a5038');
  return trim(p);
}

/* ================= Prague Castle and St. Vitus Cathedral (floodlit gold) ================= */
const CASTLE_WINS = [];                              // palace windows that switch on and off
const fl = y => 0.34 * smooth(18, 96, y);            // the floodlights reach higher up the stone the lower it is
function renderCastle() {
  const p = pix(W, 130);
  const S = (x, y, v, soft = 0.5) => p.set(x, y, pick(STONE, v, x, y, soft));
  // --- the choir: forest of flying buttresses and pinnacles, the apse at the east end (right)
  for (let x = 242; x <= 270; x++) {
    const ridge = x <= 262 ? 63 : 63 + (x - 262) * 1.15;                              // the roof hips down over the apse
    for (let y = Math.round(ridge); y < 92; y++) {
      let v;
      if (y < 72) {                                                                  // steep roof, a sheen along the ridge
        v = 0.16 + (y - ridge < 1 ? 0.18 : 0) + 0.04 * ((x + y) % 3 === 0);
        p.set(x, y, pick(SLATE, v * 2.2, x, y));
        continue;
      }
      v = 0.3 + fl(y) + (x > 264 ? -0.1 : 0);
      if (y === 72) v += 0.3;                                                         // the eaves catch the floodlight
      else if (y >= 75 && y <= 88 && (x - 243) % 4 === 1) v = 0.12;                   // tall windows between the piers
      else if ((x - 243) % 4 === 3) v += 0.14;                                        // piers
      if (y === 81) v += 0.22;
      S(x, y, v);
    }
  }
  for (let px0 = 244; px0 <= 268; px0 += 4) {                                          // pinnacles and flying buttresses
    const tip = 68 + ((px0 >> 2) & 1) * 2;
    for (let y = tip; y < 82; y++) S(px0, y, y === tip ? 0.95 : 0.55 + fl(y) * 0.5);
    S(px0, tip - 1, 0.7);
    for (let k = 0; k < 4; k++) S(px0 - 1 - k, 74 + k + (k > 1 ? 1 : 0), 0.5 + fl(76) * 0.5);   // arched strut to the wall
    for (let y = 78; y < 82; y++) S(px0 + 1, y, 0.2);
  }
  for (let x = 243; x <= 270; x++) { S(x, 82, 0.62 + fl(82)); S(x, 83, 0.3); }         // aisle roofline
  // --- the nave between the west spires and the main tower
  for (let x = 215; x < MT.x0; x++) for (let y = 64; y < 92; y++) {
    if (y < 72) { p.set(x, y, pick(SLATE, (0.36 + (y === 64 ? 0.4 : 0) + 0.08 * ((x + y) % 3 === 0)), x, y)); continue; }
    let v = 0.32 + fl(y);
    if (y === 72) v += 0.3; else if (y >= 75 && y <= 88 && (x - 216) % 4 === 1) v = 0.12; else if ((x - 216) % 4 === 3) v += 0.14;
    S(x, y, v);
  }
  for (const px0 of [217, 221, 225]) { for (let y = 69; y < 80; y++) S(px0, y, y === 69 ? 0.9 : 0.55); S(px0, 68, 0.66); }
  // --- the twin west spires
  SPIRES.forEach((cx, k) => {
    const o = k * 2, bodyTop = 68 + o, octTop = 57 + o, sTop = 35 + o;              // the right spire stands a little further off
    for (let y = bodyTop; y < 92; y++) for (let dx = -3; dx <= 3; dx++) {           // square tower
      let v = 0.32 + fl(y) * 0.9 + (dx === -3 ? 0.2 : dx === 3 ? -0.14 : dx === 2 ? -0.06 : 0);
      if (y === bodyTop || y === 80) v += 0.26;                                      // gallery and string course
      else if (y === bodyTop + 1 || y === 81) v -= 0.1;
      const open = (Math.abs(dx) === 1 && y >= bodyTop + 3 && y <= bodyTop + 10 && !(y === bodyTop + 3 && dx === 1)) || (dx === 0 && y >= 83 && y <= 89);
      if (open) { p.set(cx + dx, y, y < bodyTop + 5 ? '#2a1e22' : '#150f16'); continue; }
      S(cx + dx, y, v);
    }
    for (const dx of [-3, 3]) for (let y = bodyTop - 4; y < bodyTop; y++) S(cx + dx, y, y === bodyTop - 4 ? 0.92 : dx < 0 ? 0.62 : 0.34);   // corner pinnacles
    for (let y = octTop; y < bodyTop; y++) for (let dx = -2; dx <= 2; dx++) {         // slimmer octagonal belfry
      const open = dx === 0 && y >= octTop + 2 && y <= bodyTop - 2;
      S(cx + dx, y, open ? 0.04 : y === octTop ? 0.8 : 0.3 + fl(y) * 0.8 + (dx === -2 ? 0.26 : dx === 2 ? -0.12 : 0));
    }
    for (let y = sTop; y < octTop; y++) {                                              // the openwork spire with crockets
      const w = 2.2 * (y - sTop) / (octTop - sTop), crock = (y - sTop) % 3 === 1 && y > sTop + 2 ? 1 : 0;
      const xl = Math.round(cx - w - crock * 0.7), xr = Math.round(cx + w + crock * 0.7);
      for (let x = xl; x <= xr; x++) S(x, y, x === xl ? 0.6 + fl(y) : x === xr ? 0.2 : ((y + x) & 1 ? 0.3 : 0.44) + fl(y) * 0.5);
    }
    S(cx, sTop - 1, 0.8); S(cx, sTop - 2, 0.92); S(cx - 1, sTop - 2, 0.7); S(cx + 1, sTop - 2, 0.6); S(cx, sTop - 3, 0.92);   // finial cross
  });
  // --- the slim flèche over the crossing
  for (let y = 50; y < 64; y++) {
    const w = y > 59 ? 1 : 0;
    for (let dx = -w; dx <= w; dx++) p.set(250 + dx, y, dx < 0 ? CU[5] : dx > 0 ? CU[2] : (y === 56 ? GOLD[3] : y > 59 ? CU[4] : CU[3]));
  }
  p.set(250, 49, GOLD[4]); p.set(250, 48, GOLD[3]);
  // --- the main tower: Gothic shaft, Renaissance gallery and clock stage, the green copper helmet
  const cx = MT.cx;
  for (let y = 58; y < 92; y++) for (let dx = -7; dx <= 7; dx++) {
    let v = 0.46 + fl(y) + (dx <= -6 ? 0.16 : dx >= 6 ? -0.24 : 0);
    if (Math.abs(dx) === 4) v += 0.1;                                               // buttress lines
    if (y === 72 || y === 86) v += 0.24; else if (y === 73 || y === 87) v -= 0.16;
    const win = Math.abs(dx) <= 1 && y >= 62 && y <= 84 && !(y === 62 && dx !== 0) && y !== 72 && y !== 73;
    if (win) { p.set(cx + dx, y, dx === 0 && (y === 67 || y === 78) ? '#4a3a30' : y < 66 ? '#2a2026' : '#140e16'); continue; }
    S(cx + dx, y, v);
  }
  for (let dx = -8; dx <= 8; dx++) {                                                  // the gallery
    S(cx + dx, 55, 0.92 + (dx > 5 ? -0.2 : 0)); S(cx + dx, 56, (dx & 1) ? 0.22 : 0.74); S(cx + dx, 57, 0.4);
  }
  for (const dx of [-8, 8]) { S(cx + dx, 52, 0.8); S(cx + dx, 53, 0.66); S(cx + dx, 54, 0.66); p.set(cx + dx, 51, GOLD[4]); }
  for (let y = 47; y <= 54; y++) for (let dx = -6; dx <= 6; dx++) {                    // clock stage, pale Renaissance stone
    let v = 0.72 + (dx <= -5 ? 0.1 : dx >= 5 ? -0.26 : 0) + (y === 47 ? 0.14 : 0);
    S(cx + dx, y, v, 0.3);
  }
  for (let y = -2; y <= 2; y++) for (let dx = -2; dx <= 2; dx++) {                    // the gilded clock face (hands per frame)
    const d = Math.hypot(dx, y);
    if (d > 2.4) continue;
    p.set(cx + dx, 51 + y, d > 1.5 ? (dx + y < 0 ? GOLD[4] : GOLD[2]) : '#1e2240');
  }
  const HELM = [[46, 7], [45, 7.4], [44, 7.2], [43, 6.6], [42, 6], [41, 5.2], [40, 4.2], [39, 3.3], [38, 2.6]];   // bell-shaped helmet
  for (const [y, w] of HELM) for (let dx = -Math.floor(w); dx <= Math.floor(w); dx++) {
    const u = dx / w;
    let k = u < -0.55 ? 6 : u < -0.15 ? 5 : u < 0.35 ? 4 : u < 0.7 ? 3 : 2;
    if (y === 46) k = Math.max(1, k - 2);                                             // the eave in shadow
    if (y === 44 && dx % 3 === 0) k = Math.min(7, k + 1);                             // little dormer glints
    if (dx === 0 && y < 45) k = Math.min(7, k + 1);                                    // the front rib
    p.set(cx + dx, y, CU[k]);
  }
  for (let y = 34; y <= 37; y++) for (let dx = -2; dx <= 2; dx++)                       // the open lantern
    p.set(cx + dx, y, Math.abs(dx) === 2 ? (dx < 0 ? CU[5] : CU[2]) : y === 34 ? CU[4] : (dx === 0 ? '#ffe0a0' : '#e8b060'));
  for (const [y, w] of [[33, 2], [32, 2.6], [31, 2.4], [30, 1.6], [29, 1]])              // small onion
    for (let dx = -Math.floor(w); dx <= Math.floor(w); dx++) p.set(cx + dx, y, CU[dx < 0 ? 6 : dx > 0 ? 2 : 4]);
  for (let y = 23; y <= 28; y++) p.set(cx, y, y === 26 ? GOLD[3] : CU[3]);
  p.set(cx - 1, 26, GOLD[4]); p.set(cx + 1, 26, GOLD[2]);
  for (const [dx, y, k] of [[0, 22, 4], [-1, 21, 4], [0, 21, 5], [1, 21, 3], [0, 20, 4], [0, 19, 3]]) p.set(cx + dx, y, GOLD[k]);
  // The castle's skyline is a long, low band of evenly floodlit palace fronts with St. Vitus rising from its left middle:
  // Hradcany Square's palaces at the west end, the long south front, the east end stepping down the slope.
  const r = rng(515);
  /** a floodlit palace block: a slate roof roofH rows high, hipped at both ends, cream walls down to base(x), wins rows of windows */
  const block = (x0, x1, top, base, roofH, wins = 2) => {
    for (let k = 1; k <= roofH; k++) for (let x = x0 + k - 1; x <= x1 - k + 1; x++)
      p.set(x, top - k, pick(SLATE, 0.44 + (k === roofH ? 0.3 : 0) + (roofH - k) * 0.04, x, top - k));
    for (let x = x0; x <= x1; x++) for (let y = top, b = base(x); y <= b; y++) {
      let v = 0.42 + 0.4 * (y - top) / Math.max(1, b - top) + (vnoise(x / 4, y / 3, 22) - 0.5) * 0.08 + (x === x0 ? 0.12 : 0);
      if (y === top) v = 0.92; else if (y === top + 1) v = 0.22;
      p.set(x, y, pick(CREAM, v, x, y, 0.5));
    }
    for (let n = 0; n < wins; n++) for (let x = x0 + 2, wy = top + 3 + n * 5; x < x1 - 1; x += 3) {
      if (wy + 2 >= base(x)) continue;
      const lit = r() < 0.12, tog = !lit && r() < 0.08;
      for (let y = wy; y < wy + 2; y++) p.set(x, y, lit ? (y === wy ? WARMWIN[1] : WARMWIN[2]) : y === wy ? '#3a2e34' : '#221a24');
      if (tog) CASTLE_WINS.push({ x, y: wy, id: CASTLE_WINS.length, n: 3 + Math.floor(r() * 4), p: 0.5, off: r() });
    }
  };
  const onRidge = (dy, max = H) => x => Math.min(max, Math.round(ridgeY(x)) + dy);
  // --- St. George's Basilica: its twin white towers rise behind the palace, east of the choir
  for (const [tx, top, w] of [[279, 77, 3], [285, 79, 2]]) {
    for (let y = top; y < PAL.roof; y++) for (let dx = 0; dx < w; dx++) {
      const slot = dx === w >> 1 && y >= top + 2 && y <= top + 4;                    // the belfry openings
      p.set(tx + dx, y, slot ? '#2a1e22' : pick(CREAM, (dx === 0 ? 0.8 : dx === w - 1 ? 0.4 : 0.62) + fl(y) * 0.3, tx + dx, y, 0.5));
    }
    for (let k = 1; k <= 4; k++) for (let dx = k > 2 ? w >> 1 : 0; dx <= (k > 2 ? w >> 1 : w - 1); dx++) p.set(tx + dx, top - k, k === 4 ? GOLD[3] : dx === 0 ? SLATE[5] : SLATE[2]);
  }
  // --- the west end: Hradcany Square's palaces along the ridge (the Archbishop's, the gabled Schwarzenberg)
  block(141, PAL.x0 - 1, 100, onRidge(2), 4);
  block(124, 140, 102, onRidge(2), 0, 1);
  for (const gx of [124, 130, 136]) for (let k = 1; k <= 6; k++) {                 // the Schwarzenberg palace's stepped gables
    const inset = k <= 2 ? 0 : k <= 4 ? 1 : 2;
    for (let x = gx + inset; x <= Math.min(140, gx + 4) - inset; x++) p.set(x, 102 - k, pick(CREAM, (x === gx + inset ? 0.82 : 0.5) + (k === 6 ? 0.2 : 0), x, 102 - k, 0.5));
  }
  // --- the east end, stepping down the slope: the Old Royal Palace under Vladislav Hall's tall roof, the Lobkowicz
  // palace (its walls end above the sign's frame) and the Black Tower at the top of the Old Castle Steps
  block(PAL.x1 + 1, 305, 100, onRidge(3, BB.y - 4), 6);
  block(306, 322, 104, onRidge(3, BB.y - 4), 3);
  for (let x = 324; x <= 330; x++) for (let y = 106, b = Math.round(ridgeY(x)) + 2; y <= b; y++) {
    const v = (x === 324 ? 0.78 : x >= 329 ? 0.22 : 0.5) + 0.25 * (y - 106) / (b - 106) + (y === 115 ? 0.2 : 0);
    p.set(x, y, (x === 327 && (y === 109 || y === 110 || y === 119 || y === 120)) ? '#221a24' : pick(STONE, v, x, y, 0.5));
  }
  for (let k = 1; k <= 5; k++) for (let x = 324 + (k >> 1); x <= 330 - (k >> 1); x++) p.set(x, 106 - k, x === 324 + (k >> 1) ? SLATE[5] : SLATE[2]);   // its pyramid roof
  p.set(327, 100, GOLD[3]); p.set(327, 99, GOLD[4]);
  // --- the long palace front: roof with dormers and chimneys, a floodlit facade with rows of windows
  for (let y = PAL.roof; y < PAL.top; y++) for (let x = PAL.x0 + (PAL.top - y - 1); x <= PAL.x1 - (PAL.top - y - 1); x++) {
    let col = pick(SLATE, 0.44 + (y === PAL.roof ? 0.3 : 0) + (y - PAL.roof) * 0.04, x, y);
    if (y === PAL.roof + 2 && x % 12 === 5) col = '#6a5a40';                          // dormers
    p.set(x, y, col);
  }
  for (const chx of [178, 190, 206, 247, 262, 276]) { p.set(chx, PAL.roof - 1, SLATE[3]); p.set(chx + 1, PAL.roof - 1, SLATE[1]); p.set(chx, PAL.roof - 2, SLATE[4]); p.set(chx + 1, PAL.roof - 2, SLATE[2]); }
  for (let k = 0; k < 4; k++) for (let x = 216 + k; x <= 234 - k; x++) p.set(x, PAL.roof - 1 - k, k === 3 ? CREAM[6] : pick(CREAM, 0.55 - k * 0.08, x, k));   // the central attic
  for (let y = PAL.top; y <= PAL.base; y++) for (let x = PAL.x0; x <= PAL.x1; x++) {
    let v = 0.42 + 0.42 * (y - PAL.top) / (PAL.base - PAL.top) + (vnoise(x / 4, y / 3, 21) - 0.5) * 0.08;
    if (x >= 214 && x <= 236) v += 0.06;                                              // the central risalit
    if (x === 214 || x === 236) v -= 0.18;
    if (y === PAL.top) v = 0.96; else if (y === PAL.top + 1) v = 0.22;
    else if (y === 106) v += 0.18; else if (y === 107) v -= 0.12;
    else if (y >= PAL.base - 2) v = 0.5 + ((x >> 2) + y & 1 ? 0.08 : -0.08);          // rusticated plinth
    p.set(x, y, pick(CREAM, v, x, y, 0.5));
  }
  for (const wy of [98, 102, 109, 113]) for (let x = PAL.x0 + 2; x < PAL.x1 - 1; x += 3) {
    if (x === 214 || x === 236) continue;
    const lit = r() < 0.12, tog = !lit && r() < 0.08;
    for (let y = wy; y < wy + 2; y++) p.set(x, y, lit ? (y === wy ? WARMWIN[1] : WARMWIN[2]) : y === wy ? '#3a2e34' : '#221a24');
    if (tog) CASTLE_WINS.push({ x, y: wy, id: CASTLE_WINS.length, n: 3 + Math.floor(r() * 4), p: 0.5, off: r() });
  }
  // --- ramparts and retaining walls under the palace (the garden terraces start below)
  for (let x = 166; x <= 322; x++) for (let y0 = Math.max(PAL.base + 1, Math.min(BB.y - 4, Math.round(ridgeY(x)) + 3) - 5), y = y0; y <= y0 + 5; y++) {
    const edge = y === y0;                                                            // stepping down the slope at the east end
    let v = edge ? 0.62 : 0.3 + 0.1 * ((x + (y >> 1) * 3) % 6 === 0 ? -1 : 0) + 0.1 * (y - y0 + 1) / 6;
    if ((x - 166) % 18 === 0) v += 0.2;                                               // buttresses
    S(x, y, v);
  }
  return p.done();
}

/* ================= Mala Strana: roofs climbing the castle hill, St. Nicholas, the far bank, Kampa ================= */
const TOWN_WINS = [];                                // windows that switch on and off
const FAR_LAMPS = [];                                // street lamps along the far embankment (and Kampa)
/** one house: walls lit from the street below, a tiled roof band or a gable, 1 px windows (build time) */
function house(p, r, x0, w, base, wallH, roofH, big, opts = {}) {
  const pal = WALLS[Math.floor(r() * WALLS.length)], top = base - wallH, gable = !opts.flat && r() < 0.3;
  for (let y = top; y < base; y++) for (let x = x0; x < x0 + w; x++) {
    let v = 0.2 + 0.55 * smooth(top - 2, base + 4, y) + (x === x0 ? -0.18 : 0) + (opts.lit || 0);
    if (opts.flat && y === top) v += 0.3;
    p.set(x, y, pick(pal, v, x, y, 0.6));
  }
  const wy0 = top + 2, wh = big ? 2 : 1;
  for (let wy = wy0; wy + wh <= base - 1; wy += big ? 4 : 3) for (let wx = x0 + 1 + (w & 1); wx < x0 + w - 1; wx += big ? 3 : 2) {
    const v = r(), lit = v < (opts.pLit ?? 0.32), tog = !lit && v < (opts.pLit ?? 0.32) + 0.06;
    for (let k = 0; k < wh; k++) p.set(wx, wy + k, lit ? WARMWIN[k ? 2 : 1] : '#131120');
    if (tog) TOWN_WINS.push({ x: wx, y: wy, h: wh, id: TOWN_WINS.length, n: 2 + Math.floor(r() * 5), p: 0.45 + r() * 0.3, off: r() });
  }
  if (opts.flat) return;
  if (gable) {                                                                         // a gable facing the river
    const gh = Math.min(roofH + 2, Math.ceil(w / 2));
    for (let k = 1; k <= gh; k++) for (let x = x0 + k - 1; x <= x0 + w - k; x++)
      p.set(x, top - k, x === x0 + k - 1 ? pick(pal, 0.5, x, top - k) : pick(pal, 0.28 + 0.1 * (k === gh), x, top - k));
    if (w > 6) p.set(x0 + (w >> 1), top - Math.ceil(gh / 2), r() < 0.5 ? WARMWIN[1] : '#131120');
  } else for (let k = 1; k <= roofH; k++) for (let x = x0 + k - 1; x <= x0 + w - k; x++) {   // a hipped roof, ridge along the street
    let col = pick(ROOF, 0.25 + 0.5 * (k === roofH) + 0.2 * (1 - k / roofH) * 0.5, x, top - k);
    if (k === 2 && roofH > 3 && (x - x0) % 5 === 2) col = r() < 0.35 ? '#e8b060' : '#2a1a1a';   // dormers
    p.set(x, top - k, col);
  }
}
/** St. Nicholas: the great green dome on its drum, the lantern, and the bell tower with its copper cap */
function paintNicholas(p) {
  const cx = SN.cx, S = (x, y, v) => p.set(x, y, pick(CREAM, v, x, y, 0.5));
  for (let y = 120; y < 142; y++) for (let x = cx - 12; x <= cx + 12; x++) {           // the church under the dome
    let v = 0.3 + 0.35 * (y - 120) / 22 + (x < cx - 8 ? 0.1 : x > cx + 8 ? -0.12 : 0);
    if (y === 120) v = 0.7;
    if ((x - cx + 12) % 5 === 2 && y > 124 && y < 136) v = 0.06;
    S(x, y, v);
  }
  for (let y = 111; y < 120; y++) for (let x = cx - 8; x <= cx + 8; x++) {             // the drum with tall windows
    let v = 0.5 + (x < cx - 5 ? 0.14 : x > cx + 5 ? -0.2 : 0) + (y === 111 ? 0.3 : 0);
    if ((x - cx + 8) % 3 === 1 && y > 112 && y < 119) v = 0.08;
    S(x, y, v);
  }
  for (let y = 101; y < 111; y++) for (let x = cx - 9; x <= cx + 9; x++) {             // the dome (half ellipse), copper ribs
    const u = (x - cx) / 9.4, vv = (111 - y) / 10;
    if (u * u + vv * vv > 1) continue;
    let k = u < -0.5 ? 6 : u < -0.1 ? 5 : u < 0.4 ? 4 : u < 0.75 ? 3 : 2;
    if ((x - cx) % 3 === 0 && y > 102) k = Math.max(1, k - 1);
    if (y === 110) k = Math.min(7, k + 1);
    p.set(x, y, CU[k]);
  }
  for (let y = 95; y < 101; y++) for (let x = cx - 1; x <= cx + 1; x++) p.set(x, y, y === 95 ? CU[4] : x === cx ? (y > 96 ? '#ffe0a0' : CU[5]) : x < cx ? CU[5] : CU[2]);   // lantern
  p.set(cx, 94, CU[4]); p.set(cx, 93, GOLD[3]); p.set(cx, 92, GOLD[4]); p.set(cx - 1, 92, GOLD[2]); p.set(cx + 1, 92, GOLD[2]); p.set(cx, 91, GOLD[3]);
  const tx = cx + 18;                                                                    // the bell tower
  for (let y = 108; y < 142; y++) for (let x = tx - 3; x <= tx + 3; x++) {
    let v = 0.46 + 0.3 * (y - 108) / 34 + (x === tx - 3 ? 0.18 : x >= tx + 2 ? -0.24 : 0);
    if (y === 108 || y === 122) v += 0.3;
    if (x === tx && ((y > 110 && y < 118) || (y > 125 && y < 131))) v = 0.05;
    S(x, y, v);
  }
  for (const [y, w] of [[107, 4], [106, 4], [105, 3.6], [104, 3], [103, 2.2], [102, 1.4], [101, 1]])
    for (let dx = -Math.floor(w); dx <= Math.floor(w); dx++) p.set(tx + dx, y, CU[dx < -1 ? 6 : dx < 1 ? 4 : 2]);
  for (let y = 97; y < 101; y++) p.set(tx, y, y === 98 ? CU[5] : CU[3]);
  p.set(tx, 96, GOLD[4]); p.set(tx, 95, GOLD[3]);
}
function renderTown() {
  const p = pix(W, H), r = rng(909);
  const row = (x0, x1, base, wMin, wMax, hMin, hMax, big, skip) => {
    let x = x0;
    while (x < x1) {
      const w = wMin + Math.floor(r() * (wMax - wMin + 1)), b = base + (x > 300 ? Math.round((x - 300) * 0.22) : 0) + Math.floor(r() * 3) - 1;
      if (!skip || !skip(x, w)) house(p, r, x, w, b, hMin + Math.floor(r() * (hMax - hMin + 1)), 3 + Math.floor(r() * 3), big);
      x += w;
    }
  };
  row(104, 176, 132, 6, 12, 6, 9, false);
  row(176, 396, 137, 7, 13, 6, 9, false);
  paintNicholas(p);
  row(100, 396, 146, 7, 14, 7, 10, false);
  row(96, 396, 157, 8, 15, 8, 11, true, (x, w) => x + w > BB.x - 6 && x < BB.x + BB.w + 6);
  // the tall house by the river that carries the rooftop sign: a flat roof with a cornice
  house(p, r, BB.x - 5, BB.w + 10, 168, 168 - BB_ROOF, 0, true, { flat: true, pLit: 0.36, lit: 0.06 });
  row(96, 396, 168, 8, 16, 9, 12, true);
  // the far riverside row downstream, its embankment wall and street lamps
  row(118, 396, FWL - 2, 9, 16, 11, 15, true);
  for (let x = 118; x < 396; x++) {
    p.set(x, FWL - 2, '#4a4038'); p.set(x, FWL - 1, '#2a2426'); p.set(x, FWL, '#1c1a20');
  }
  for (let y = 144; y < FWL - 2; y++) for (let x = 96; x < 400; x++) {             // river haze over the far bank behind the bridge
    const f = 0.5 * smooth(146, 162, y) * (1 - 0.5 * smooth(168, 178, y));
    if (!f || !p.alpha(x, y)) continue;
    const c = p.get(x, y);
    p.set(x, y, [0, 1, 2].map(i => Math.round(lerp(c[i], [62, 56, 72][i], f))));
  }
  for (let x = 124; x < 396; x += 13) { FAR_LAMPS.push({ x, y: FWL - 5, a: 0.55 }); p.set(x, FWL - 5, '#ffe0a0'); p.set(x, FWL - 4, '#2a2426'); p.set(x, FWL - 3, '#2a2426'); }
  // Kampa: tall old houses along the water and big trees in the park
  let x = -2;
  while (x < 122) {
    const w = 10 + Math.floor(r() * 8);
    house(p, r, x, w, KWL - 2, 13 + Math.floor(r() * 7), 4 + Math.floor(r() * 3), true, { pLit: 0.38 });
    x += w;
  }
  for (const [tx, ty, rr] of [[30, 164, 12], [58, 160, 14], [84, 166, 11]]) {
    for (let y = ty - rr; y < KWL - 2; y++) for (let i = tx - rr - 2; i <= tx + rr + 2; i++) {
      const dx = (i - tx) / (rr * 1.15), dy = (y - ty) / rr, n = vnoise(i / 2.5, y / 2.2, tx);
      const v = 1 - dx * dx - dy * dy + (n - 0.5) * 0.7;
      if (y > ty + rr * 0.5) { if (Math.abs(i - tx) <= 1) p.set(i, y, i < tx ? '#2a2018' : '#120e0c'); continue; }
      if (v <= 0 || (v < 0.12 && bayer(i, y) > 0.5)) continue;
      const lit = clamp(dy * 0.9 + (n - 0.5) * 0.6, 0, 1);
      p.set(i, y, lit > 0.45 ? pick(LEAFLIT, (lit - 0.45) * 1.3, i, y) : pick(LEAF, 0.15 + n * 0.5, i, y));
    }
  }
  for (let x = 0; x < 120; x++) { p.set(x, KWL - 2, '#4a4038'); p.set(x, KWL - 1, '#2a2426'); p.set(x, KWL, '#1a1820'); }
  for (const lx of [12, 44, 72, 102]) { FAR_LAMPS.push({ x: lx, y: KWL - 7, a: 0.8 }); p.set(lx, KWL - 7, '#ffe6a8'); for (let y = KWL - 6; y < KWL - 1; y++) p.set(lx, y, '#1e1c22'); }
  return trim(p);
}
/** the Lesser Town Bridge Towers at the far end: the tall Gothic tower, the gate and little Judith's Tower */
function renderLesserTowers() {
  const p = pix(W, H), S = (x, y, v) => p.set(x, y, pick(STONE, v, x, y, 0.5));
  // Judith's Tower: older and lower, weathered Romanesque stone (darker than the houses round it), paired round-headed
  // windows, and its own little pyramid roof under a gilded knob, a clear break in the roofline next to the tall tower
  const OLD = ['#141118', '#1c181e', '#262024', '#322a2a', '#40342e', '#504034', '#624e3a', '#7a6242', '#98784c'];
  const J0 = 103, J1 = 111, JC = 107, JT = 152;
  for (let y = JT; y < 174; y++) for (let x = J0; x <= J1; x++) {
    let v = 0.3 + 0.44 * (y - JT) / (174 - JT) + (x === J0 ? 0.32 : x === J0 + 1 ? 0.1 : x >= J1 - 1 ? -0.12 : 0);
    if (y === JT) v += 0.34; else if (y === JT + 1) v -= 0.1;                          // cornice under the eaves
    else if (y === 163) v += 0.2; else if (y === 164) v -= 0.08;                        // a string course
    else if (hash(x, y >> 1, 57) < 0.2) v -= 0.1;                                       // rough, weathered ashlar
    const twin = y >= 155 && y <= 158 && x >= JC - 2 && x <= JC + 2 && x !== JC && !(y === 155 && (x === JC - 2 || x === JC + 2));
    if (twin) v = y === 155 ? 0.1 : 0;                                                   // the paired windows
    if (x === JC && y >= 167 && y <= 169) v = 0;                                         // an arrow slit below
    p.set(x, y, pick(OLD, v, x, y, 0.5));
  }
  for (let x = J0 - 1; x <= J1 + 1; x++) p.set(x, JT - 1, x <= J0 ? STONE[7] : x >= J1 ? STONE[3] : STONE[5]);   // the eaves
  for (let y = JT - 8; y < JT - 1; y++) {                                               // the pyramid roof, slate, lit up its left hip
    const w = Math.round(5 * (y - (JT - 9)) / 8), xl = JC - w, xr = JC + w;
    for (let x = xl; x <= xr; x++) p.set(x, y, x === xl ? STONE[8] : x === xr ? '#5a5670' : pick(SLATE, 0.3 + (x < JC ? 0.22 : 0) + ((x + y) % 3 === 0 ? 0.12 : 0), x, y));
  }
  p.set(JC, JT - 9, SLATE[5]); p.set(JC, JT - 10, GOLD[3]); p.set(JC, JT - 11, GOLD[4]);
  for (let y = 158; y < 174; y++) for (let x = 112; x <= 116; x++) {                    // the gate between them
    const arch = y >= 164 && x >= 113 && x <= 115 && !(y === 164 && x !== 114);
    S(x, y, arch ? (y > 167 ? 0.5 : 0.04) : 0.44 + 0.2 * (y - 158) / 16);
  }
  for (let x = 112; x <= 116; x++) p.set(x, 157, SLATE[3]);
  const x0 = 117, x1 = 129, cx = 123;
  for (let y = 146; y < 174; y++) for (let x = x0; x <= x1; x++) {                      // the tall tower
    let v = 0.44 + 0.34 * (y - 146) / 28 + (x === x0 ? 0.2 : x >= x1 - 1 ? -0.22 : 0);
    if (y === 146 || y === 160) v += 0.3; else if (y === 147) v -= 0.14;
    if ((x === cx - 2 || x === cx + 2) && y >= 150 && y <= 157 && !(y === 150)) v = 0.04;
    if (x === cx && y >= 163 && y <= 168) v = 0.06;
    S(x, y, v);
  }
  for (let y = 128; y < 146; y++) {                                                      // steep slate roof, its edges catching the floodlight
    const w = 5.6 * (y - 128) / 18, xl = Math.round(cx - w), xr = Math.round(cx + w);
    for (let x = xl; x <= xr; x++) p.set(x, y, x === xl ? STONE[8] : x === xr ? STONE[4] : pick(['#2e2a36', '#3e3846', '#50485a', '#645a6a'], 0.35 + 0.4 * (y - 128) / 18 + ((x + y) % 3 === 0 ? 0.15 : 0) + (x < cx ? 0.12 : 0), x, y));
  }
  for (const tx of [x0, x1]) {                                                           // corner turrets
    for (let y = 139; y < 146; y++) p.set(tx, y, tx === x0 ? STONE[8] : STONE[5]);
    p.set(tx, 138, STONE[6]); p.set(tx, 137, SLATE[5]); p.set(tx, 136, GOLD[3]);
  }
  p.set(cx, 127, GOLD[3]); p.set(cx, 126, GOLD[4]);
  return trim(p);
}
/** the lower pool beyond the bridge: dark water with the far bank's lights broken up in it */
function renderFarWater() {
  const p = pix(W, 30);
  for (let y = 0; y < 30; y++) for (let x = 0; x < W; x++) {
    let v = 0.2 + 0.5 * y / 30 + (vnoise(x / 30, y / 2, 44) - 0.5) * 0.14;
    const warm = FAR_LAMPS.reduce((m, l) => l.y < FWL ? Math.max(m, Math.exp(-(((x - l.x) / 0.8) ** 2)) * Math.max(0, 1 - y / 12) * 0.7) : m, 0);
    let col = pick(['#1c2440', '#172038', '#131a30', '#0f1628', '#0c1222', '#0a0f1c'], v, x, y);
    if (warm > 0.3 && hash(x, y, 7) < warm && y % 2 === 0) col = y < 4 ? '#c8905a' : '#6a5440';
    if (Math.abs(x - 228) < 60 && y < 12 && hash(x >> 1, y, 8) < 0.1 * (1 - y / 12)) col = '#5a4a3a';   // the castle's glow
    p.set(x, y, col);
  }
  return p.done();
}

/* ================= The rooftop sign (the city's one billboard) =================
   An old-style sign on low iron legs on the roof of a riverside house below the castle, framed by a ring of bulbs
   that chase round it. Its own ad is for an invented trdelnik stand: a sugared roll turning on its spit over glowing
   coals, and the words TRDELNIK / U MOSTU ("by the bridge"). BB is the face. */
const BB_BULBS = [];                                 // the ring of bulbs around the face, in order round the frame
{
  const L = BB.x - 2, R = BB.x + BB.w + 1, T = BB.y - 2, B = BB.y + BB.h + 1;
  for (let x = L; x < R; x += 3) BB_BULBS.push([x, T]);
  for (let y = T; y < B; y += 3) BB_BULBS.push([R, y]);
  for (let x = R; x > L; x -= 3) BB_BULBS.push([x, B]);
  for (let y = B; y > T; y -= 3) BB_BULBS.push([L, y]);
}
const ROLL = { x: 2, y: 6, w: 10, h: 7 };            // the trdelnik on its spit, in the face
function renderBoard() {
  const p = pix(W, H), L = BB.x - 2, R = BB.x + BB.w + 1, T = BB.y - 2, B = BB.y + BB.h + 1;
  for (let y = T; y <= B; y++) for (let x = L; x <= R; x++) {                          // the iron frame the bulbs sit on
    const edge = y === T || y === B || x === L || x === R;
    p.set(x, y, edge ? (y === T ? '#3a3440' : x === L ? '#2e2a36' : '#141218') : '#0c0a10');
  }
  for (const lx of [BB.x + 3, BB.x + (BB.w >> 1), BB.x + BB.w - 4]) {                // legs and braces down to the roof
    for (let y = B + 1; y < BB_ROOF; y++) { p.set(lx, y, '#1a1820'); p.set(lx + 1, y, '#0c0b10'); }
  }
  for (let x = BB.x + 4; x < BB.x + BB.w - 4; x++) if ((x + B) % 4 < 2) p.set(x, B + 1 + ((x >> 1) & 1), '#15131a');
  return trim(p);
}
/** the ad itself (static part): oxblood enamel, a gold rule, the words */
function renderAd() {
  const { w, h } = BB, p = pix(w, h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    let col = pick(['#2a0c14', '#3a1019', '#4c1620', '#601c26', '#76242c'], 0.72 - j / h * 0.6 + 0.12 * Math.exp(-(((i - 7) / 7) ** 2) - (((j - 14) / 5) ** 2)), i, j, 0.7);
    if (i === 0 || j === 0 || i === w - 1 || j === h - 1) col = j === 0 ? '#f6c860' : '#c8902e';
    p.set(i, j, col);
  }
  for (let i = ROLL.x - 1; i <= ROLL.x + ROLL.w + 1; i++) p.set(i, ROLL.y + 3, '#9a9aa8');   // the spit
  p.set(ROLL.x - 1, ROLL.y + 2, '#6a6a74'); p.set(ROLL.x - 1, ROLL.y + 4, '#6a6a74');
  for (const bx of [ROLL.x + 1, ROLL.x + ROLL.w - 2]) for (let y = ROLL.y + ROLL.h; y < h - 3; y++) p.set(bx, y, '#3a3440');   // stand
  for (let i = ROLL.x; i < ROLL.x + ROLL.w; i++) p.set(i, h - 3, '#1a0a0a');            // the grate
  const c = p.done();
  text(p.x, 'TRDELNIK', 14, 3, '#fff0cc');
  text(p.x, 'U MOSTU', 16, 11, '#f4b440');
  return c;
}
const ROLL_COLS = ['#f0c070', '#d89448', '#b06a2c', '#7a4418'];
function drawAd(t) {
  const X = BB.x + ROLL.x, Y = BB.y + ROLL.y, sp = step(t, 1440);          // the roll turns: its spiral grooves march along
  for (let j = 0; j < ROLL.h; j++) for (let i = 0; i < ROLL.w; i++) {
    const shade = j === 0 ? 0 : j >= ROLL.h - 2 ? 3 : j === 1 ? 1 : 2;
    const groove = ((i + j + sp) % 4) === 0;
    ctx.fillStyle = groove ? ROLL_COLS[Math.min(3, shade + 1)] : ROLL_COLS[shade];
    if (!(i === 0 && (j === 0 || j === ROLL.h - 1)) && !(i === ROLL.w - 1 && (j === 0 || j === ROLL.h - 1))) ctx.fillRect(X + i, Y + j, 1, 1);
  }
  ctx.fillStyle = '#fff4dc';                                                             // sugar glints
  for (let k = 0; k < 3; k++) if (hash(k, step(t, 720), 3) < 0.5) ctx.fillRect(X + 1 + k * 3 + (sp & 1), Y + 1, 1, 1);
  for (let k = 0; k < 4; k++) {                                                           // coals
    const v = hash(k, step(t, 960), 5);
    ctx.fillStyle = v < 0.3 ? '#ffd060' : v < 0.7 ? '#ff7a30' : '#c83a20';
    ctx.fillRect(X + 1 + k * 2 + (k & 1), BB.y + BB.h - 4, 1, 1);
  }
  ctx.fillStyle = '#e8dcd0';                                                             // steam curling up
  for (let k = 0; k < 3; k++) {
    const u = frac(t * 24 / LOOP + k / 3), sx = X + 3 + k * 3 + Math.round(wave(t, 36, k * 0.3) * u * 1.5);
    ctx.globalAlpha = 0.55 * (1 - u); ctx.fillRect(sx, Y - 1 - Math.round(u * 4), 1, 1);
  }
  ctx.globalAlpha = 1;
}
const BULB_ON = ['#fff2c0', '#ffd878'], BULB_OFF = '#4a3222';
function drawBoard(t, pitch) {
  halo(HL.bbGlow, BB.x + (BB.w >> 1), BB.y + (BB.h >> 1), 0.9 + 0.08 * wave(t, 8, 0.2));
  blit(BOARD_C);
  if (!PITCH.covers(t, pitch)) { ctx.drawImage(AD_C, BB.x, BB.y); drawAd(t); }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  const ch = step(t, 1920), n = BB_BULBS.length;                                       // the chase: every third bulb dark, marching
  for (let i = 0; i < n; i++) {
    const [x, y] = BB_BULBS[i], on = (i + ch) % 3 !== 0;
    dot(x, y, on ? BULB_ON[i & 1] : BULB_OFF);
    if (on && (i & 1)) halo(HL.bbBulb, x, y, 0.7);
  }
}

/* ================= Charles Bridge: sixteen arches glowing underneath, cutwaters, statues, lamps ================= */
const BR_LAMPS = [];                                 // lanterns along the parapet { x, y, sc }
const STATUE_KIND = ['saint', 'group', 'saint', 'cross', 'saint', 'saint', 'group', 'nepomuk', 'saint', 'group', 'saint', 'saint', 'group', 'saint', 'saint'];
// [what a saint holds (0 a hand raised in blessing, 1 a staff or cross, 2 a book), the side it's on (a group: its smaller figure)]
const STATUE_POSE = [[1, 1], [0, 1], [0, -1], [0, 0], [2, 1], [0, 1], [0, -1], [0, 0], [1, -1], [0, 1], [0, 1], [2, -1], [0, -1], [0, -1], [1, 1]];
const NEPO = [];                                     // St. John of Nepomuk's five gold stars (they twinkle)
const PW = 0.17;                                     // half a pier, as a share of one span
function renderBridge() {
  const p = pix(W, H), S = (x, y, v) => p.set(x, y, pick(BRIDGE, v, x, y, 0.6));
  for (let x = XL - 1; x < OT.x0 + 3; x++) {
    const s = sAtX(x + 0.5), sc = bsc(s), rail = Math.round(bRail(s)), deck = Math.round(bDeck(s)), wl = Math.round(bWater(s));
    const f = s * NARCH, i = Math.floor(f), u = f - i;
    const open = u > PW && u < 1 - PW && i < NARCH, uu = open ? (u - 0.5) / (0.5 - PW) : 0;
    const archTop = open ? wl - 21 * sc * Math.pow(Math.max(0, 1 - uu * uu), 0.5) : 999;
    const dp = u < 0.5 ? u : u - 1;                                                    // signed distance to the nearest pier (spans)
    const cwH = 9 * sc, cwTop = wl - cwH * (0.62 + 0.38 * (1 - Math.min(1, Math.abs(dp) / PW)));
    for (let y = rail; y <= wl; y++) {
      if (y >= archTop) {                                                              // the arch: its soffit glows, then the far water
        const k = y - archTop;
        if (k < 1) p.set(x, y, '#f0a850'); else if (k < 2 + sc) p.set(x, y, k < 2 ? '#b8743a' : '#6a4028');
        continue;
      }
      let v;
      if (y === rail) v = 0.8;
      else if (y === rail + 1) v = 0.36;
      else if (y < deck) v = 0.3 + ((x + y) % 5 === 0 ? -0.08 : 0);
      else if (y === deck) v = 0.66;
      else if (y === deck + 1) v = 0.12;
      else {
        v = 0.24 + 0.42 * (y - deck) / (wl - deck);                                   // floodlit from the water
        if (open && archTop - y < 1.6 + 1.6 * sc) v += 0.26;                          // voussoirs around the arch
        const course = Math.max(2, Math.round(3 * sc));
        if ((y - deck) % course === 0) v -= 0.1;
        if (!open && y >= cwTop) {                                                     // the cutwater in front of the pier
          v = dp > 0 ? 0.62 + 0.2 * (y - cwTop) / cwH : 0.32;
          if (y < cwTop + 1) v = 0.86;
        }
      }
      S(x, y, v);
    }
  }
  // statues on their pedestals on the parapet over the piers, lamps between them
  for (let i = 1; i < NARCH; i++) {
    const s = i / NARCH;
    paintStatue(p, STATUE_KIND[i - 1], Math.round(bxAt(s)), Math.round(bRail(s)), bsc(s), i);
  }
  for (let i = 0; i < NARCH; i++) {
    const s = (i + 0.5) / NARCH;
    if (bxAt(s) > OT.x0 - 2) continue;
    const sc = bsc(s), x = Math.round(bxAt(s)), rail = Math.round(bRail(s)), hgt = Math.round(9 * sc) + 1;
    for (let y = rail - hgt; y < rail; y++) p.set(x, y, y > rail - 3 ? '#3a2c24' : '#1a1418');
    const big = sc > 0.72, lh = big ? 3 : 2;
    p.set(x, rail - hgt - lh - 1, '#2a2226'); if (big) p.set(x + 1, rail - hgt - lh - 1, '#1a1418');
    for (let y = rail - hgt - lh; y < rail - hgt; y++) { p.set(x, y, y === rail - hgt - lh ? '#fffae8' : '#ffe6a8'); if (big) p.set(x + 1, y, '#f4c070'); }
    BR_LAMPS.push({ x: x + (big ? 0.5 : 0), y: rail - hgt - lh / 2, sc });
  }
  return trim(p);
}
/** a dark baroque statue (or group) on its pedestal. The figures are built as a mask first (head, shoulders, a swaying
 *  body, a robe that flares onto the plinth), then coloured: soot-dark stone, a warm rim down the side the bridge lamps
 *  catch, a cool moonlit edge on the other, and a thin dark backing that keeps the lit windows behind from eating into
 *  the silhouette. St. John of Nepomuk (kind 'nepomuk') wears his ring of five gold stars. */
const ST_K = '#100b12', ST_GLINT = '#c08650', ST_RIM = ['#e8ac6a', '#4e3226'], ST_TOP = '#3e405c', ST_COOL = '#262b44', ST_BACK = [10, 12, 26];
function paintStatue(p, kind, x, rail, sc, n) {
  const big = sc > 0.62, fh = Math.max(6, Math.round(12 * sc)), pedH = Math.max(3, Math.round(4.6 * sc)), base = rail - pedH;
  const M = new Map(), put = (i, j, v = 1) => { const k = j * W + i; if (v === 2 || !M.has(k)) M.set(k, v); };
  const [attr, side] = STATUE_POSE[n - 1];
  /** one figure, c = its head's (left) column; wide: a 2 px head on 4 px shoulders, else 1 px on 3 */
  const figure = (c, h, wide, sway = 1) => {
    const top = base - h, hw = wide ? 2 : 1, sh = h >= 10 ? 2 : 1, robe = h >= 10 ? 3 : 2;
    for (let r = 0; r < h; r++) {
      let l = c - 1, rr = c + hw;                                                       // shoulders and robe
      if (r < hw) { l = c; rr = c + hw - 1; }                                            // head
      else if (r >= hw + sh && r < h - robe) { l = c - 1 + (sway > 0 ? 1 : 0); rr = l + hw; }   // the body sways
      for (let i = l; i <= rr; i++) put(i, top + r, r < hw ? 3 : 1);
    }
    return { top, l: c - 1, r: c + hw, sh: top + hw };
  };
  if (kind === 'group') {                                                               // a saint, a smaller figure at his side
    const f = figure(side > 0 ? (big ? x - 2 : x - 1) : (big ? x + 1 : x + 2), fh, big, -side), h2 = fh - (big ? 3 : 2);
    figure(side > 0 ? f.r + 2 : f.l - 2, h2, false, side);
    const hx = side > 0 ? f.r + 1 : f.l - 1;
    put(hx, f.sh - 1); put(hx, f.sh);                                                   // his hand raised over it
  } else if (kind === 'cross') {                                                        // the crucifix: a gilded Christ, two mourners
    const top = base - fh - 2, bar = top + 2, aw = big ? 2 : 1;
    for (let y = top; y < base; y++) put(x, y);
    for (let k = -aw; k <= aw; k++) put(x + k, bar);
    for (let k = -aw + 1; k <= aw - 1; k++) put(x + k, bar, 2);
    for (let y = bar + 1; y <= bar + 3; y++) put(x, y, 2);
    put(x, top, 2);                                                                     // the gilded inscription
    const hm = Math.max(4, Math.round(fh * 0.5));
    figure(x - 3, hm, false, 1); figure(x + 3, hm, false, -1);
  } else if (kind === 'nepomuk') {                                                      // slim, in his cassock, under the halo
    const f = figure(x, fh, false, 0);
    for (const [dx, dy] of [[-3, -1], [-1, -3], [1, -3], [3, -1]]) put(x + dx, f.top + dy, 4);   // the gilded hoop...
    for (const [k, dx, dy] of [[0, -3, 0], [1, -2, -2], [2, 0, -3], [3, 2, -2], [4, 3, 0]]) {     // ...and its five stars
      put(x + dx, f.top + dy, 2); NEPO.push({ x: x + dx, y: f.top + dy, k });
    }
  } else {
    const f = figure(big ? x - 1 : x, fh, big, side);
    const ax = side > 0 ? f.r + 1 : f.l - 1;
    if (attr === 0) for (let k = 0; k < (big ? 3 : 2); k++) put(ax + (k === 2 ? side : 0), f.sh - k);   // a hand raised in blessing
    else if (attr === 1) { for (let y = f.top - (big ? 3 : 2); y < base - 2; y++) put(ax, y); if (big) { put(ax - 1, f.top - 2); put(ax + 1, f.top - 2); } }   // a cross-staff
    else put(ax, f.sh + 1 + (big ? 1 : 0));                                                // a book held out
  }
  // the pedestal: a moulded cornice, the die, a plinth on the parapet
  let x0 = W, x1 = -1;
  for (const k of M.keys()) { const i = k % W, j = (k - i) / W; if (j === base - 1) { x0 = Math.min(x0, i); x1 = Math.max(x1, i); } }
  if (x1 - x0 < 2) { x0 = x - 1; x1 = x + 1; }
  for (let y = base; y < rail; y++) {
    const edge = y === base || (y === rail - 1 && pedH >= 4), l = edge ? x0 - 1 : x0, r = edge ? x1 + 1 : x1;
    for (let i = l; i <= r; i++) p.set(i, y, y === base ? (i <= l + 1 ? '#d0a86c' : i === r ? '#5a4434' : '#a8845a')
      : i === l ? (edge ? '#7a5c40' : '#8a6a48') : i === r ? '#2a201c' : edge ? '#4a3a2c' : y === base + 1 ? '#3e3028' : '#584432');
  }
  // the backing: a thin, dark, cool veil around the silhouette (and the stars), over the lit town behind
  const seen = new Set();
  for (const k of M.keys()) for (const d of [-W - 1, -W, -W + 1, -1, 1, W - 1, W, W + 1]) {
    const q = k + d, i = q % W, j = (q - i) / W;
    if (M.has(q) || seen.has(q) || j >= base) continue;
    seen.add(q); p.set(i, j, ST_BACK, 130);
  }
  // the figures: dark stone; the lamps catch the heads and every corner of the drapery on the left (a faint warm glow
  // runs down between them), the moon touches the tops and the right-hand edges
  for (const [k, v] of M) {
    const i = k % W, j = (k - i) / W;
    if (v === 2 || v === 4) { p.set(i, j, v === 2 ? GOLD[4] : '#7a5622'); continue; }
    const L = !M.has(k - 1), R = !M.has(k + 1), T = !M.has(k - W);
    p.set(i, j, v === 3 ? (L && T && !R ? ST_GLINT : ST_K) : L ? ST_RIM[T ? 0 : 1] : T ? ST_TOP : R ? ST_COOL : ST_K);
  }
}

/* ================= The Old Town Bridge Tower at our end of the bridge ================= */
function renderOldTown() {
  const p = pix(W, H), S = (x, y, v) => p.set(x, y, pick(STONE, v, x, y, 0.5)), { x0, x1, cx, top, base } = OT;
  for (let y = top; y <= base; y++) for (let x = x0; x <= x1; x++) {
    let v = 0.42 + 0.36 * (y - top) / (base - top) + (x <= x0 + 1 ? 0.18 : x >= x1 - 1 ? -0.26 : x >= x1 - 3 ? -0.1 : 0);
    if (y === 140 || y === 162 || y === 180) v += 0.3; else if (y === 141 || y === 163 || y === 181) v -= 0.18;
    if ((x === x0 + 3 || x === x1 - 3) && y > 120) v += 0.08;                        // corner buttresses
    for (const wx of [cx - 7, cx + 6]) {                                            // tall blind windows, upper storey
      if (y >= 123 && y <= 137 && x >= wx && x <= wx + 2 && !(y === 123 && x !== wx + 1)) v = x === wx + 1 && y === 129 ? 0.3 : 0.06;
    }
    if (y >= 145 && y <= 159 && x >= cx - 2 && x <= cx + 2 && !(y === 145 && x !== cx) && !(y === 146 && Math.abs(x - cx) > 1)) {
      v = (x === cx || y === 152) ? 0.34 : 0.06;                                     // the great pointed window
    }
    S(x, y, v);
  }
  for (let k = 0; k < 5; k++) {                                                        // coats of arms along the frieze
    const hx = x0 + 3 + k * 5;
    p.set(hx, 165, ['#c8302a', GOLD[3], '#2a4a9a', GOLD[3], '#c8302a'][k]); p.set(hx + 1, 165, GOLD[4]);
    p.set(hx, 166, ['#8a1c1a', GOLD[2], '#1c3470', GOLD[2], '#8a1c1a'][k]); p.set(hx + 1, 166, GOLD[2]);
  }
  for (let x = x0 - 2; x <= x1 + 2; x++) {                                             // the gallery at the top
    S(x, top - 4, 0.92 + (x > x1 - 2 ? -0.3 : 0)); S(x, top - 3, (x & 1) ? 0.1 : 0.66); S(x, top - 2, (x & 1) ? 0.1 : 0.56); S(x, top - 1, 0.34);
  }
  for (let y = top - 32; y < top - 4; y++) {                                           // the steep roof, slightly concave
    const u = (y - (top - 32)) / 28, w = 11 * Math.pow(u, 1.25);
    const xl = Math.round(cx - w), xr = Math.round(cx + w);
    for (let x = xl; x <= xr; x++) {
      let col = pick(SLATE, 0.3 + ((x - cx + 40) % 4 === 0 ? 0.18 : 0) + (x < cx - w * 0.5 ? 0.12 : 0), x, y);
      if (x === xl) col = SLATE[5];
      if (x === xr) col = SLATE[0];
      p.set(x, y, col);
    }
  }
  for (const dx of [-4, 4]) { p.set(cx + dx, top - 12, '#6a5a44'); p.set(cx + dx, top - 11, '#2a2228'); }   // dormers
  for (let y = top - 38; y < top - 32; y++) p.set(cx, y, y < top - 35 ? GOLD[3] : SLATE[4]);
  p.set(cx, top - 39, GOLD[4]);
  for (const tx of [x0 - 2, x1]) {                                                      // corner turrets with pointed caps
    for (let y = top - 16; y < top - 4; y++) for (let k = 0; k < 3; k++) S(tx + k, y, (k === 0 ? 0.72 : k === 1 ? 0.52 : 0.28) + 0.1 * (y - top + 16) / 12);
    for (let k = 0; k < 6; k++) { const w = Math.floor(k / 2.5); for (let dx = -w; dx <= w; dx++) p.set(tx + 1 + dx, top - 17 - (5 - k), dx < 0 ? SLATE[4] : SLATE[1]); }
    p.set(tx + 1, top - 23, GOLD[3]);
  }
  return trim(p);
}

/* ================= The Novotneho lavka pier: a lit terrace on the water, the old museum building ================= */
const TERRACE_BULBS = [];                            // festoon lights over the terrace
function renderLavka() {
  const p = pix(W, H), r = rng(606);
  for (let y = 146; y < 200; y++) for (let x = 420; x < W; x++) {                       // the neo-Renaissance house behind
    let v = 0.14 + 0.36 * (y - 146) / 54 + (x === 420 ? 0.2 : 0);
    if (y === 146 || y === 170 || y === 186) v += 0.3; else if (y === 147 || y === 171) v -= 0.14;
    const sg = ((x + y * 2) % 7 === 0 || (x * 3 + y) % 11 === 0) && y > 148 && y < 168;  // sgraffito
    let col = pick(CREAM, v - (sg ? 0.28 : 0), x, y, 0.5);
    const wx = (x - 424) % 8, wy1 = y >= 152 && y <= 163, wy2 = y >= 174 && y <= 183;
    if ((wy1 || wy2) && wx >= 0 && wx < 3 && x < W - 1) col = (wy1 && y < 154) || (wy2 && y < 176) ? '#3a2a24' : hash(x >> 3, y > 170 ? 1 : 0, 5) < 0.6 ? (y % 3 ? WARMWIN[1] : WARMWIN[2]) : '#141220';
    p.set(x, y, col);
  }
  for (let k = 0; k < 10; k++) for (let x = 420 + k; x <= 444 - k; x++) p.set(x, 145 - k, k === 9 ? CREAM[4] : pick(CREAM, 0.2 - k * 0.01 + (x < 432 ? 0.1 : 0), x, k));   // gable
  for (let x = 444; x < W; x++) for (let y = 141; y < 146; y++) p.set(x, y, pick(SLATE, 0.3 + (y === 141 ? 0.3 : 0), x, y));
  for (let y = 190; y < NL.deck; y++) for (let x = NL.x0 + 4; x < W; x++) p.set(x, y, pick(['#1c1612', '#2a2018', '#3a2c1e'], 0.3 + 0.4 * (y - 190) / 14, x, y));   // the terrace in shadow
  for (const [ux, col] of [[396, '#d8c8a8'], [414, '#b8342c'], [434, '#d8c8a8'], [458, '#b8342c'], [474, '#d8c8a8']]) {   // parasols
    for (let k = 0; k < 3; k++) for (let x = ux - 5 + k * 2; x <= ux + 5 - k * 2; x++) p.set(x, 193 - k, k === 0 ? mixHex(col, '#ffb060', 0.3) : mixHex(col, '#000000', 0.3 + k * 0.15));
    for (let y = 194; y < NL.deck; y++) p.set(ux, y, '#2a2420');
  }
  for (let x = NL.x0 + 5; x < W; x += 7) {                                              // diners and candles
    const hx = x + Math.floor(r() * 3);
    p.set(hx, 199, '#1a1210'); p.set(hx, 200, '#241814'); p.set(hx - 1, 201, '#241814'); p.set(hx, 201, '#241814'); p.set(hx + 1, 201, '#241814');
    p.set(hx + 3, 201, '#fff0c0'); for (let k = 1; k < 5; k++) p.set(hx + k, 202, '#3a2a1e');
  }
  for (let x = NL.x0; x < W; x++) {                                                     // railing, deck edge and the pier's stone front
    p.set(x, NL.deck - 1, x % 3 === 0 ? '#5a4a3a' : '#2a221c'); p.set(x, NL.deck, '#8a7a62');
    for (let y = NL.deck + 1; y <= NL.wl; y++) {
      const blk = (y - NL.deck) % 3 === 0 || (x + ((y - NL.deck) / 3 | 0) * 4) % 9 === 0;
      p.set(x, y, blk ? '#161418' : pick(['#221e22', '#2e2828', '#3c3430'], 0.3 + 0.5 * Math.max(0, 1 - (y - NL.deck) / 8) + (x < NL.x0 + 2 ? 0.2 : 0), x, y));
    }
  }
  for (let k = 0; k < 30; k++) {                                                        // festoon bulbs in swags between the parasol poles
    const x = NL.x0 + 6 + k * 3.1, seg = ((x - NL.x0 - 6) % 23) / 23;
    TERRACE_BULBS.push({ x: Math.round(x), y: Math.round(187 + Math.sin(Math.PI * seg) * 3), k: 5 + (k * 7) % 13, ph: hash(k, 9) });
  }
  return trim(p);
}

/* ================= The river: water, the mirrored bridge, the weir ================= */
const WY0 = 180;                                     // top of the water canvases
const wlAt = x => x >= NL.x0 ? NL.wl + 1 : x >= XL - 1 ? Math.round(bWater(sAtX(x + 0.5))) + 1 : KWL + 1;
function renderWater() {
  const p = pix(W, WB - WY0);
  const P = ['#212a46', '#1b2440', '#161e36', '#12192e', '#0e1426', '#0b101e', '#090d19'];
  for (let x = 0; x < W; x++) for (let y = wlAt(x); y < WB; y++) {
    const down = x < WEIR_X1 && y < weirY(x);                                          // below the weir: livelier, paler water
    let v = (y - 184) / (WB - 184) * 0.95 + (vnoise(x / 36, y / 3, 9) - 0.5) * 0.14 - (down ? 0.18 : 0);
    let col = pick(P, v, x, y);
    if (down && hash(x >> 1, y, 31) < 0.08) col = '#4a5876';
    p.set(x, y - WY0, col);
  }
  return p.done();
}
/** the mirrored scene under the waterline of each column, darkened into the water (built once from the static layers) */
function renderReflection(comp) {
  const src = comp.getContext('2d').getImageData(0, 0, W, H).data, p = pix(W, WB - WY0), tint = [12, 18, 36];
  for (let x = 0; x < W; x++) {
    const wl = wlAt(x), bridge = x >= XL - 1 && x < NL.x0 ? wl - Math.round(bRail(sAtX(x + 0.5))) : 6;   // rows of bridge above the water
    for (let y = wl; y < WB; y++) {
      const k = y - wl, sy = wl - 2 - k;
      if (sy < 0) break;
      const past = Math.max(0, k - bridge), o = (sy * W + x) * 4, a = 0.74 * Math.max(0, 1 - k / 44) * Math.max(0.25, 1 - past / 10);
      p.set(x, y - WY0, [0, 1, 2].map(i => Math.round(lerp(src[o + i], tint[i], past ? 0.6 : 0.36) * 0.84)), Math.round(a * 255));
    }
  }
  return p.done();
}
/**
 * The weir in WEIR_NF frames, one cycle of its foam's drift (shown 4 a second): a dim lip catching the light, broken
 * bands of foam tumbling slowly away downstream, faint streaks further out. Smooth noise, periodic over the cycle,
 * moves it a fraction of a pixel per frame; stretches of the crest foam more than others.
 */
const WEIR_TOP = 184, WEIR_NF = 32;
function renderWeir(f) {
  const p = pix(WEIR_X1 + 1, 36), ph = f / WEIR_NF;
  for (let x = 0; x <= WEIR_X1; x++) {
    const wy = Math.round(weirY(x)) - WEIR_TOP, reach = 0.3 + 0.7 * vnoise(x / 24, 0.5, 43);
    const lip = reach * (0.5 + 0.5 * vnoise(ph * 6, x / 5, 44, 6));
    if (lip > 0.28 + hash(x, wy, 47) * 0.4) p.set(x, wy, lip > 0.62 ? '#7a8aae' : '#56668c', 220);
    p.set(x, wy + 1, [8, 12, 24], 110);                                                    // the smooth tongue before the drop
    for (let k = 1; k <= 4; k++) {
      const d = reach * (0.35 + 0.65 * vnoise(k * 0.8 - ph * 5, x / 3.5, 45, 5)) * (1.2 - k * 0.22);
      if (d > 0.26 + hash(x, wy - k, 48) * 0.36) p.set(x, wy - k, d > 0.62 ? '#a4b0cc' : d > 0.45 ? '#7c8aac' : '#5a6890', 200);
    }
    if (hash(x >> 2, 3, 13) < 0.3) for (let k = 5; k <= 10; k++) {                       // streaks in a few lanes further out
      if (reach * vnoise(k * 0.5 - ph * 4, x / 7, 46, 4) > 0.5 + hash(x, wy - k, 49) * 0.3) p.set(x, wy - k, '#4e5c80', 150 - k * 8);
    }
  }
  return p.done();
}

/* ================= Swans, ducks, the paddle steamer ================= */
const SWAN_KEY = { W: '#eef0f4', w: '#9aa2b8', g: '#c8ccd8', K: '#15151c', O: '#e07a30' };
const SWAN = ['........WW.', '........WKO', '........W..', '.......gW..', '.......W...', '..WWW..W...', '.WWWWWWWW..', 'WWWWWWWWW..', '.wwwwwww...'];
const SWAN_DIP = ['...........', '...........', '...........', '...........', '...........', '..gWW......', '.WWWWWWW...', 'WWWWWWWWWW.', '.wwwwwwwwW.'];
const DUCK_KEY = { g: '#1c4a30', G: '#2e6a44', y: '#c8a040', B: '#6a6a70', b: '#3a3a44', W: '#e8e8ec', h: '#5a4230', H: '#7a5a3a' };
const DUCKS = { drake: ['...gG', '...Gy', 'BBBW.', '.bbb.'], hen: ['...hH', '...Hy', 'hHhh.', '.hhh.'] };
const SWANS = [
  { cx: 214, cy: 222, ax: 70, ay: 2, kx: 1, ky: 3, px: 0.1, py: 0.2, dips: [[30, 37], [128, 136], [201, 206]] },
  { cx: 226, cy: 225, ax: 62, ay: 2, kx: 1, ky: 2, px: 0.16, py: 0.5, dips: [[82, 88], [168, 175]] },
  { cx: 300, cy: 218, ax: 40, ay: 1, kx: 2, ky: 3, px: 0.7, py: 0.1, dips: [[60, 66], [150, 154], [222, 228]] },
];
const DUCKS_AT = [{ kind: 'drake', cx: 66, cy: 204, ax: 34, ay: 3, kx: 2, ky: 5, px: 0.3, py: 0.1 }, { kind: 'hen', cx: 64, cy: 207, ax: 33, ay: 3, kx: 2, ky: 5, px: 0.27, py: 0.4 }];
const ST_W = 106, ST_H = 30;
const ST_LIGHTS = [[4, 20, 12, 3], [12, 3, 57, 0], [58, 0, 99, 5], [99, 5, 104, 19]];   // the strings of bulbs, bow to stern
function renderSteamer() {
  const p = pix(ST_W, ST_H);
  for (let y = 21; y < ST_H; y++) {                                                      // hull: raked bow (left), round stern
    const x0 = Math.round((y - 21) * 0.8), x1 = ST_W - 2 - Math.max(0, y - 25);
    for (let x = x0; x <= x1; x++) p.set(x, y, y === 21 ? '#f8f6f0' : y <= 25 ? (y === 23 && x % 7 === 3 ? '#3a3a46' : '#dcdad2') : y === 26 ? '#9a2c26' : '#1a1c26');
  }
  for (let y = 15; y < 21; y++) for (let x = 7; x < ST_W - 6; x++) {                     // the saloon, a row of warm windows
    let col = y === 15 ? '#f4f2ea' : '#e2e0d8';
    if (y >= 16 && y <= 19 && (x - 9) % 4 < 2 && x < ST_W - 8) col = y === 16 ? '#fff2c8' : y === 19 ? '#e89a4a' : '#ffd07a';
    if (y === 20) col = '#9a988e';
    p.set(x, y, col);
  }
  for (let x = 20; x < ST_W - 12; x++) { p.set(x, 14, x % 2 ? '#e8e6de' : '#6a6a70'); if (x % 4 === 0) p.set(x, 13, '#e8e6de'); }   // upper deck rail
  for (let x = 28; x < 88; x++) { p.set(x, 9, '#efe4c8'); p.set(x, 10, (x & 1) ? '#d8ccae' : '#efe4c8'); if (x & 1) p.set(x, 11, '#c8bc9e'); }   // canopy, scalloped
  for (const [hx, col] of [[31, '#6a3a3a'], [36, '#2a3a5a'], [41, '#5a5a3a'], [66, '#3a2a4a'], [71, '#7a4a2a'], [78, '#2a4a3a'], [83, '#5a2a2a']]) {
    p.set(hx, 11, '#e0a882'); p.set(hx, 12, col); p.set(hx + 1, 12, col); p.set(hx, 13, col); p.set(hx + 1, 13, col);   // passengers
  }
  for (let y = 7; y < 15; y++) for (let x = 44; x < 54; x++) p.set(x, y, y === 7 ? '#f8f6f0' : (y >= 9 && y <= 11 && x % 3 !== 2) ? '#ffe2a0' : '#e4e2da');   // wheelhouse
  for (let y = 0; y < 9; y++) for (let x = 56; x < 60; x++) p.set(x + (y < 4 ? 1 : 0), y, y < 2 ? '#15151a' : y === 4 ? '#a8302a' : x === 56 ? '#f4e8c8' : '#d8c8a2');   // funnel
  for (let dy = -9; dy <= 0; dy++) for (let dx = -9; dx <= 9; dx++) {                    // the paddle box: a half sunburst
    const d = Math.hypot(dx, dy);
    if (d > 9.4) continue;
    const ang = Math.atan2(-dy, dx), ray = Math.abs(((ang / Math.PI) * 9) % 1 - 0.5) < 0.16;
    p.set(65 + dx, 22 + dy, d > 8.3 ? '#8a6a3a' : d < 2 ? GOLD[3] : ray ? '#8a8478' : (dx < 0 ? '#f4f0e6' : '#dcd8cc'));
  }
  for (let y = 3; y < 15; y++) p.set(12, y, '#3a3a44');                                  // bow mast, stern flagstaff and a flag
  for (let y = 5; y < 15; y++) p.set(99, y, '#3a3a44');
  for (const [fx, fy, col] of [[100, 5, '#f4f4f4'], [101, 5, '#f4f4f4'], [102, 5, '#f4f4f4'], [100, 6, '#2a4aa8'], [101, 6, '#d82a2a'], [102, 6, '#d82a2a'], [100, 7, '#2a4aa8'], [101, 7, '#2a4aa8'], [102, 7, '#d82a2a']])
    p.set(fx, fy, col);
  p.set(12, 2, '#ffffff'); p.set(3, 22, '#ff4a3a'); p.set(ST_W - 3, 22, '#ffffff');       // masthead, side and stern lights
  return p.done();
}

/* ================= Foreground: the balustrade, the bench, the embankment street, the old lamp ================= */
const lampWarm = (x, y) => Math.exp(-Math.hypot((x - LP.x) / 60, (y - LP.y - 40) / 70));
function renderBalustrade() {
  const p = pix(W, H);
  for (let x = 0; x < W; x++) {
    const lw = lampWarm(x, 232), post = x % 48 < 4;
    p.set(x, BAL, pick(['#5a5048', '#7a6c5c', '#9a8872', '#bca47e'], 0.4 + 0.6 * lw, x, BAL));
    p.set(x, BAL + 1, pick(['#3a342e', '#524840', '#6a5a48'], 0.4 + 0.5 * lw, x, BAL + 1));
    p.set(x, BAL + 2, '#221e1c');
    for (let y = BAL + 3; y < SIDE - 3; y++) {
      if (post) { p.set(x, y, pick(['#2a2622', '#3e3630', '#56483c'], (x % 48 === 0 ? 0.8 : 0.35) + 0.4 * lw, x, y)); continue; }
      const m = x % 4, j = y - BAL - 3, prof = [1, 0, 1, 1, 1, 0, 1][j] ?? 1;      // vase-shaped balusters, the water between them
      const dx = m - 1.5;
      if (Math.abs(dx) > prof + 0.5) continue;
      p.set(x, y, pick(['#1e1a1a', '#2e2824', '#443a32', '#5e5040'], (dx < 0 ? 0.62 : 0.26) + 0.4 * lw, x, y));
    }
    for (let y = SIDE - 3; y < SIDE; y++) p.set(x, y, pick(['#1a1818', '#2a2624', '#3e3630', '#54483c'], (y === SIDE - 3 ? 0.66 : 0.26) + 0.4 * lw, x, y));
  }
  return trim(p);
}
function renderStreet() {
  const p = pix(W, H);
  for (let x = 0; x < W; x++) for (let y = SIDE; y < H; y++) {
    const lw = lampWarm(x, y);
    let col;
    if (y < KERB) col = pick(['#15151b', '#1e1e25', '#28272e', '#3a3432'], 0.3 + 0.5 * lw + ((x + (y - SIDE) * 7) % 23 === 0 ? -0.3 : 0), x, y);
    else if (y === KERB) col = pick(['#3a3a42', '#56545a', '#7a7068'], 0.4 + 0.6 * lw, x, y);
    else if (y < PAVE - 1) {                                                          // granite setts, the two rails
      const row = Math.floor((y - KERB) / 3), sx = (y - KERB) % 3 === 0 || (x + (row & 1) * 2) % 4 === 0 ? -0.1 : 0;
      col = pick(['#0e0e13', '#15151b', '#1c1b22', '#26242a', '#3a3230', '#54443a'], 0.25 + 0.7 * lw + sx + (hash(x >> 1, y >> 1, 3) - 0.5) * 0.12, x, y);
      if (y === RAIL_F || y === RAIL_N) col = pick(['#3a3c46', '#5a5c68', '#8a8a94', '#c8b494'], 0.35 + 0.8 * lw + (hash(x >> 3, y, 5) < 0.2 ? 0.2 : 0), x, y);
      else if (y === RAIL_F + 1 || y === RAIL_N + 1) col = '#08080c';
    } else if (y === PAVE - 1) col = pick(['#3a3a44', '#5a5860', '#7e7468'], 0.4 + 0.6 * lw, x, y);
    else {                                                                              // the mosaic pavement: pale stones in waves
      const band = Math.sin(x * 0.19 + Math.sin(y * 0.9) * 1.2) > 0.35;
      col = pick(band ? ['#2a2a30', '#3e3c42', '#5a5654', '#7a6e62'] : ['#0e0e12', '#16161b', '#1e1e24', '#2c2a2c'], 0.3 + 0.6 * lw + ((x ^ y) & 1 ? -0.08 : 0), x, y);
    }
    p.set(x, y, col);
  }
  return p.done();
}
/** the bench against the balustrade, seen from behind (the couple is drawn per frame) */
function renderBench() {
  const p = pix(W, H), { x, w } = BENCH;
  for (let i = x; i < x + w; i++) {
    p.set(i, 232, '#6a4a30'); p.set(i, 233, '#3a2818'); p.set(i, 235, '#5a3e28'); p.set(i, 236, '#2e2014');
    p.set(i, 239, '#4a3422'); p.set(i, 240, '#1e150e');
  }
  for (const lx of [x + 1, x + w - 2]) for (let y = 231; y < SIDE + 3; y++) { p.set(lx, y, y < 234 ? '#2a2830' : '#15141a'); p.set(lx + 1, y, '#0c0b10'); }
  for (let y = 241; y < SIDE + 3; y++) for (const lx of [x + 1, x + w - 2]) p.set(lx + (y > 243 ? (lx < x + 5 ? -1 : 1) : 0), y, '#15141a');
  return trim(p);
}
const COUPLE = { him: '#1a1f2e', hair: '#141016', her: '#3a1e2a', herHair: '#3a2418', skin: '#8a6a58' };
function drawCouple(t) {
  const { x } = BENCH, lean = LEAN.some(([a, b]) => t >= a && t < b), Y = 5;         // seated: heads just above the coping
  const hx = x + 11, sx = x + 20;
  box(hx - 2, 225 + Y, 6, 8 - Y + 2, COUPLE.him); box(hx - 1, 224 + Y, 4, 1, COUPLE.him);   // him: shoulders, head
  box(hx, 220 + Y, 3, 4, COUPLE.hair); dot(hx + 1, 219 + Y, COUPLE.hair);
  ctx.globalAlpha = 0.5; box(hx, 220 + Y, 1, 3, '#6a5a50'); box(hx - 2, 225 + Y, 1, 3, '#4a4a5a'); ctx.globalAlpha = 1;   // rim light
  const lx = lean ? sx - 1 : sx, ly = (lean ? 222 : 221) + Y;                           // her head rests on his shoulder
  box(sx - 1, 226 + Y, 5, 7 - Y + 2, COUPLE.her); box(sx, 225 + Y, 3, 1, COUPLE.her);
  box(lx, ly, 3, 3, COUPLE.herHair); box(lx, ly + 3, 1, 3, COUPLE.herHair); box(lx + 2, ly + 3, 1, 2, COUPLE.herHair);
  ctx.globalAlpha = 0.45; box(lx, ly, 1, 2, '#7a5a48'); ctx.globalAlpha = 1;
  if (!lean && t % 60 > 20 && t % 60 < 23) { dot(hx + 3, 227 + Y, COUPLE.him); dot(hx + 4, 226 + Y, COUPLE.him); }   // he points out a boat
}
/** the old lamp post: a stepped cast-iron base, a fluted column, a four-sided lantern under a pointed crown */
function renderLampPost() {
  const p = pix(W, H), { x: cx, base } = LP;
  const I = (x, y, k) => p.set(x, y, ['#0a0a0e', '#15151b', '#23232c', '#34343e', '#4a4a56'][k]);
  for (const [y0, y1, hw] of [[base - 2, base + 1, 5], [base - 6, base - 3, 4], [base - 10, base - 7, 3], [base - 14, base - 11, 2]])
    for (let y = y0; y <= y1; y++) for (let dx = -hw; dx <= hw; dx++) I(cx + dx, y, y === y0 ? 3 : dx === -hw ? 3 : dx >= hw - 1 ? 0 : 1);
  for (let y = LP.y + 17; y < base - 14; y++) for (let dx = -1; dx <= 1; dx++) I(cx + dx, y, dx < 0 ? 3 : dx === 0 ? 2 : 0);
  for (const ry of [230, 206, 186]) for (let dx = -2; dx <= 2; dx++) { I(cx + dx, ry, dx < 0 ? 4 : 1); I(cx + dx, ry + 1, 0); }
  for (let x = cx - 14; x < cx - 1; x++) I(x, WIRE_R - 1, x === cx - 14 ? 3 : 2);         // the bracket that holds the wire
  for (let k = 0; k < 5; k++) I(cx - 2 - k, WIRE_R - 2 - Math.round(Math.sin(k / 4 * Math.PI) * 2), 2);
  I(cx - 12, WIRE_R, 3);
  for (let y = LP.y + 13; y < LP.y + 17; y++) for (let dx = -(y - LP.y - 11); dx <= y - LP.y - 11; dx++) I(cx + dx, y, dx < 0 ? 3 : 1);   // collar
  for (let y = LP.y - 6; y <= LP.y + 12; y++) {                                        // the lantern
    const hw = Math.round(lerp(5, 3, (y - LP.y + 6) / 18));
    for (let dx = -hw; dx <= hw; dx++) {
      const frame = Math.abs(dx) === hw || dx === 0 || y === LP.y - 6 || y === LP.y + 12;
      if (frame) { I(cx + dx, y, dx === -hw ? 3 : 1); continue; }
      const f = (y - LP.y + 6) / 18;
      p.set(cx + dx, y, f < 0.3 ? '#fff6d8' : f < 0.7 ? '#ffe0a0' : '#f4b868');
    }
  }
  for (let k = 0; k < 8; k++) for (let dx = -(7 - k); dx <= 7 - k; dx++) I(cx + dx, LP.y - 7 - k, dx === -(7 - k) ? 4 : dx < 0 ? 2 : 1);   // crown
  I(cx, LP.y - 15, 3); I(cx, LP.y - 16, 4); I(cx, LP.y - 17, 2);
  for (let y = WIRE_L; y < PAVE + 2; y++) { I(5, y, 3); I(6, y, 1); I(7, y, 0); }       // a plain pole at the left end of the wire
  for (let x = 6; x < 12; x++) I(x, WIRE_L - 1, 2);
  return trim(p);
}
function renderWire() {
  const p = pix(W, H);
  for (let x = 0; x < WIRE_X1; x++) p.set(x, wireY(x), '#0e1016', 220);
  return trim(p);
}

/* ================= The tram: a red-and-cream Tatra, facing right (flipped for the other way) ================= */
function renderTram() {
  const w = TRAM.w, h = 33, p = pix(w, h);
  const RED = ['#6a1614', '#8e1e1c', '#b82a26', '#d8413a', '#ee6a58'], CRM = ['#b8a888', '#dcccaa', '#f2e6c8'];
  const inset = y => y === 0 ? 4 : y === 1 ? 2 : y === 2 ? 1 : y >= 29 ? 3 : y >= 27 ? 1 : 0;
  const DOORS = [[14, 21], [60, 67], [104, 111]];
  for (let y = 0; y < 31; y++) for (let x = inset(y); x < w - inset(y); x++) {
    let col;
    if (y <= 1) col = y === 0 ? '#b0b0b8' : '#7a7a84';                                   // roof
    else if (y <= 3) col = y === 2 ? RED[4] : RED[3];
    else if (y <= 13) col = CRM[1];
    else if (y <= 15) col = y === 14 ? CRM[2] : CRM[0];
    else if (y <= 26) col = y === 16 ? RED[3] : y >= 24 ? RED[1] : RED[2];
    else col = y >= 29 ? '#101014' : '#1c1c22';
    const door = DOORS.find(([a, b]) => x >= a && x <= b);
    if (door && y >= 3 && y <= 27) {
      const [a, b] = door, mid = (a + b) >> 1;
      col = x === a || x === b || x === mid ? RED[1] : (y >= 5 && y <= 12) || (y >= 16 && y <= 22) ? (y < 8 || (y > 15 && y < 18) ? '#fbeec8' : '#e8cc90') : RED[2];
    } else if (y >= 5 && y <= 12 && x > 6 && x < w - 12) {                              // windows between the pillars
      const m = (x - 7) % 10;
      if (m < 8) col = y === 5 ? '#fff6dc' : y > 10 ? '#e8cc90' : '#fbeec8';
    }
    if (x >= w - 11 && y >= 4 && y <= 13) col = x === w - 11 || y === 4 ? CRM[2] : (x + y) % 5 === 0 ? '#5a6a84' : '#2e3a50';   // windscreen
    if (x < 6 && y >= 5 && y <= 12) col = x === 5 ? CRM[2] : '#2e3a50';                                                       // rear window
    p.set(x, y, col);
  }
  const r = rng(71);
  for (let x = 8; x < w - 14; x += 10) if (!DOORS.some(([a, b]) => x >= a - 2 && x <= b + 1) && r() < 0.7) {   // passengers
    const hx = x + 1 + Math.floor(r() * 5), col = ['#4a3a34', '#2e3040', '#5a3a2a', '#3a4a3a'][Math.floor(r() * 4)];
    p.set(hx, 8, '#3a2a22'); p.set(hx + 1, 8, '#3a2a22'); p.set(hx, 9, '#c8966e'); p.set(hx + 1, 9, '#b07a58');
    for (let y = 10; y <= 12; y++) for (let k = -1; k <= 2; k++) p.set(hx + k, y, col);
  }
  for (let x = w - 10; x < w - 3; x++) { p.set(x, 2, '#1a1410'); p.set(x, 3, x % 2 ? '#ffb030' : '#6a3a10'); }   // route sign
  p.set(w - 2, 19, '#fff8e0'); p.set(w - 3, 19, '#fff0c0'); p.set(w - 2, 20, '#ffe0a0');                        // headlight
  p.set(1, 19, '#ff3a30'); p.set(1, 20, '#c8201a');                                                            // tail light
  for (const bx of [22, 100]) for (let x = bx; x < bx + 14; x++) { p.set(x, 31, x % 7 < 4 ? '#2a2a30' : '#141418'); p.set(x, 32, '#0a0a0e'); }   // bogies
  return p.done();
}

/* ================= Linden branches framing the top, heavy with blossom ================= */
function renderBranch(side) {
  const p = pix(W, 84), r = rng(side ? 717 : 313), leaves = [];
  const limb = (x0, y0, ang, len, wd, depth) => {
    let xx = x0, yy = y0, a = ang;
    for (let s = 0; s < len; s++) {
      a += (r() - 0.5) * 0.22 + (side ? 0.004 : -0.004);
      xx += Math.cos(a); yy += Math.sin(a);
      const w = Math.max(1, Math.round(wd * (1 - s / len * 0.75)));
      for (let k = 0; k < w; k++) p.set(Math.round(xx), Math.round(yy) + k, k === 0 && w > 1 ? '#1e2024' : '#0a0b0d');
      if (depth < 3 && s > 4 && r() < 0.1) limb(xx, yy, a + (r() < 0.5 ? 0.6 : -0.5) + (r() - 0.5) * 0.3, len * (0.3 + r() * 0.35), w, depth + 1);
      if (s > len * 0.2 && r() < 0.34) leaves.push([xx + (r() - 0.5) * 10, yy + 1 + r() * 8]);
    }
    for (let k = 0; k < 7; k++) leaves.push([xx + (r() - 0.5) * 12, yy + (r() - 0.3) * 9]);
  };
  if (side) { limb(W + 3, 6, Math.PI + 0.12, 96, 4, 0); limb(W + 3, -3, Math.PI - 0.06, 70, 3, 1); limb(W + 2, 30, Math.PI + 0.5, 40, 2, 2); }
  else { limb(-3, 4, 0.1, 104, 4, 0); limb(-3, -2, 0.02, 62, 2, 1); limb(-3, 24, 0.45, 36, 2, 2); }
  for (const [lx, ly] of leaves) {                                                     // heart-shaped leaves, lit from below near the lamp
    const X = Math.round(lx), Y = Math.round(ly), k = r(), warm = side ? clamp(1 - Math.hypot(X - LP.x, Y - 120) / 150, 0, 1) : 0.15;
    const dark = k < 0.5 ? LEAF[1] : k < 0.88 ? LEAF[2] : LEAF[4];
    const lit = warm > 0.35 && r() < warm ? LEAFLIT[Math.min(4, Math.floor(warm * 5))] : dark;
    for (const [dx, dy] of [[0, 0], [1, 0], [-1, 0], [0, 1], [1, 1], [0, -1]]) p.set(X + dx, Y + dy, dy > 0 ? lit : dark);
    if (r() < 0.1) {                                                                     // a blossom cluster on its pale bract
      p.set(X + 2, Y + 1, BRACT[1]); p.set(X + 3, Y + 2, BRACT[0]);
      p.set(X + 1, Y + 3, BLOSSOM[1]); p.set(X + 2, Y + 3, BLOSSOM[2]); p.set(X + 1, Y + 4, BLOSSOM[0]);
    }
  }
  return trim(p);
}

/* ================= Linden blossoms: sprites for the three depths ================= */
function renderBlossomNear(k, n) {
  const s = 13, c0 = 6, p = pix(s, s), a = TAU * k / n, ca = Math.cos(a), sa = Math.sin(a);
  for (let j = -3.5; j <= 3.5; j += 0.5) {                                              // the wing-like bract, a lit edge along it
    const x = c0 + ca * j, y = c0 + sa * j;
    p.set(x, y, BRACT[Math.abs(j) > 2.5 ? 1 : 2]);
    if (Math.abs(j) <= 2) p.set(x + sa * 0.9, y - ca * 0.9, BRACT[3]);
  }
  const mx = c0 + ca * 0.5, my = c0 + sa * 0.5, fx = mx - sa * 2.6, fy = my + ca * 2.6;   // the flowers hang off its middle
  p.set(mx - sa * 1.3, my + ca * 1.3, '#9aa060');
  p.set(fx, fy, BLOSSOM[3]); p.set(fx + 1, fy, BLOSSOM[2]); p.set(fx, fy + 1, BLOSSOM[2]); p.set(fx - 1, fy, BLOSSOM[1]); p.set(fx, fy - 1, BLOSSOM[2]);
  return p.done();
}
function renderBlossomMid(k) {
  const p = pix(5, 5), a = Math.PI * k / 4, ca = Math.cos(a), sa = Math.sin(a);
  for (let j = -1; j <= 1; j++) p.set(2 + ca * j, 2 + sa * j, BRACT[j + 2]);
  p.set(2 - sa * 1.6, 2 + ca * 1.6, BLOSSOM[3]);
  return p.done();
}
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.44) / (H / 2) * 0.92));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [6, 8, 18], Math.round(q * 0.4 * 255));
  }
  return p.done();
}

/* ================= Build-time particle and life tables ================= */
const BLOSSOMS = [[], [], []];                       // linden blossoms: far (over Mala Strana), mid (onto the river), near
{
  const r = rng(6262);
  [[64, 3, 5, 6, 14], [38, 5, 7, 12, 28], [13, 9, 13, 18, 50]].forEach(([count, nMin, nMax, amp, drift], layer) => {
    for (let i = 0; i < count; i++) BLOSSOMS[layer].push({ id: layer * 100 + i, n: nMin + Math.floor(r() * (nMax - nMin + 1)), ph: r(),
      ks: 10 + Math.floor(r() * 20), p2: r(), kspin: (300 + Math.floor(r() * 420)) * (r() < 0.5 ? 1 : -1), p4: r(),
      amp: amp * (0.6 + r() * 0.8), drift: drift * (0.5 + r()), land: r(), tone: r() });
  });
}
const FLOATERS = [];                                 // blossoms already floating on the river
{ const r = rng(6363); for (let i = 0; i < 26; i++) FLOATERS.push({ x: r(), y: 199 + Math.floor(r() * 28), k: 1 + Math.floor(r() * 2), ph: r() }); }
const WALKERS = [];                                  // people strolling over the bridge (only heads and shoulders show)
{
  const r = rng(8080), COATS = ['#5a4436', '#4a4a5a', '#6a3a34', '#3e4a4a', '#6a5a48', '#54405a'];
  for (let i = 0; i < 34; i++) WALKERS.push({ k: 1 + Math.floor(r() * 2), ph: r(), dir: r() < 0.5 ? 1 : -1, col: COATS[Math.floor(r() * COATS.length)],
    head: r() < 0.3 ? '#c8a07a' : '#8a6a52', pair: r() < 0.3, bob: r() });
  for (const s of [0.18, 0.31, 0.47, 0.6, 0.83]) WALKERS.push({ fixed: s, col: COATS[Math.floor(r() * COATS.length)], head: '#8a6a52', pair: s > 0.5, bob: r() });
}
// a picked weather (site/js/engine/weather.js): the linden blossoms, and the ones floating on the river, show only with Prague's
// own weather. Far drops land on the far bank and the water under the bridge, mid ones on the river in front of the
// weir, near ones on the embankment street. The bridge lamps, the floodlit towers and the terrace light the far and mid
// drops (and the sign, with the billboard); the near ones catch the old lamp post.
const WEATHER = weatherPlans(['blossoms']);
const WX_LAND = [[176, 190], [202, 238], [KERB + 1, H - 2]];
let WX, WX_BB;
function buildWx() {
  const scene = [
    ...BR_LAMPS.map(l => ({ x: l.x, y: l.y + 3, rx: 4 + 5 * l.sc, ry: 6 + 7 * l.sc, col: '#ffd890' })),
    { x: OT.cx, y: 150, rx: 20, ry: 46, col: '#ffc878' }, { x: 236, y: 104, rx: 100, ry: 22, col: '#ffd890' },
    { x: 436, y: 194, rx: 52, ry: 10, col: '#ffc870' }, { x: LP.x, y: LP.y + 4, rx: 30, ry: 36, col: '#ffe0a0' },
  ];
  const near = [{ x: LP.x, y: LP.y + 24, rx: 48, ry: 74, col: '#ffe0a0' }, { x: 436, y: 196, rx: 40, ry: 12, col: '#ffc870' }];
  const sign = { x: BB.x + (BB.w >> 1), y: BB.y + (BB.h >> 1), rx: 34, ry: 20, col: '#ffb070' };
  WX = [scene, scene, near].map(lights => ({ land: WX_LAND, lights }));
  WX_BB = [[...scene, sign], [...scene, sign], near].map(lights => ({ land: WX_LAND, lights }));
}

/* ================= Prerendered state ================= */
let HL, SKY_C, MOON_C, WISPS, HILLS, PTOWER, CASTLE, TOWN, LTOW, FARW_C, BRIDGE_C, OTOWER, LAVKA, WATER_C, REF_C, WEIR_F,
  SWAN_SPR, DUCK_SPR, STEAM_C, BAL_C, STREET_C, BENCH_C, LAMP_C, WIRE_C, TRAM_C, TRAM_F, BRANCH_L, BRANCH_R,
  BL_NEAR, BL_MID, VIG_C, BOARD_C, AD_C, PITCH, PUFF, WALKER_SPR;
function prerender() {
  for (const a of [HILL_LAMPS, CASTLE_WINS, TOWN_WINS, FAR_LAMPS, BR_LAMPS, NEPO, TERRACE_BULBS]) a.length = 0;   // lists filled while painting
  HL = {
    moon: makeHalo(44, '#8ea0d8', 0.17, 1.7), moonCore: makeHalo(12, '#f0ecd8', 0.28, 1.5),
    castle: makeHalo(78, '#ffb458', 0.12, 1.6), castleCore: makeHalo(40, '#ffd08a', 0.12, 1.4), vitus: makeHalo(30, '#ffc878', 0.14, 1.5),
    lantern: makeHalo(7, '#ffe0a0', 0.5, 1.2), ring: makeHalo(4, '#ffe0a0', 0.5, 1), petrin: makeHalo(20, '#ffc060', 0.16, 1.5),
    beacon: makeHalo(4, '#ff3a30', 0.7, 1), win: makeHalo(4, '#ffb860', 0.18, 1.3), tiny: makeHalo(3, '#ffd890', 0.35, 1),
    nicholas: makeHalo(16, '#ffc878', 0.14, 1.5), tower: makeHalo(40, '#ffb458', 0.13, 1.5), ltow: makeHalo(20, '#ffb458', 0.13, 1.5),
    brLamp: makeHalo(10, '#ffc870', 0.5, 1.3), brGlow: makeHalo(30, '#ffa850', 0.1, 1.5), star: makeHalo(2, '#ffe890', 0.3, 1),
    terrace: makeHalo(28, '#ffa850', 0.18, 1.5), bulb: makeHalo(3, '#ffd080', 0.45, 1),
    bbGlow: makeHalo(36, '#ff9a50', 0.14, 1.6), bbBulb: makeHalo(3, '#ffd878', 0.4, 1),
    lamp: makeHalo(34, '#ffc070', 0.26, 1.6), lampCore: makeHalo(10, '#fff0c8', 0.5, 1.3),
    head: makeHalo(9, '#fff4d0', 0.5, 1.2), tail: makeHalo(4, '#ff3a30', 0.5, 1), tramGlow: makeHalo(30, '#ffd890', 0.13, 1.4),
    spark: makeHalo(14, '#b8d8ff', 0.8, 1.2), steam: makeHalo(20, '#ffc070', 0.2, 1.5), stBulb: makeHalo(2, '#ffe0a0', 0.5, 1),
  };
  SKY_C = renderSky(); MOON_C = renderMoon();
  WISPS = [
    { c: makeWisps(21, 20, 9, { top: '#4a5690', body: '#2c3666', bot: '#232b56' }), y: 40, k: 1 },
    { c: makeWisps(33, 18, 7, { top: '#3e4a82', body: '#262f5c', bot: '#1f2750' }), y: 66, k: 2 },
  ];
  HILLS = renderHills(); PTOWER = renderPetrinTower(); CASTLE = renderCastle();
  TOWN = renderTown(); LTOW = renderLesserTowers(); FARW_C = renderFarWater();
  BRIDGE_C = renderBridge(); OTOWER = renderOldTown(); LAVKA = renderLavka();
  BOARD_C = renderBoard(); AD_C = renderAd(); PITCH = makePitch(BB.w, BB.h, { accent: '#7ddc82' });
  const [comp, cx] = makeCanvas(W, H);                                                     // the static scene the river mirrors
  cx.drawImage(SKY_C, 0, 0);
  for (const o of [HILLS, PTOWER]) cx.drawImage(o.c, o.x, o.y);
  cx.drawImage(CASTLE, 0, 0);
  for (const o of [TOWN, LTOW]) cx.drawImage(o.c, o.x, o.y);
  cx.drawImage(FARW_C, 0, FWL + 1);
  for (const o of [BRIDGE_C, OTOWER, LAVKA]) cx.drawImage(o.c, o.x, o.y);
  WATER_C = renderWater(); REF_C = renderReflection(comp);
  WEIR_F = Array.from({ length: WEIR_NF }, (_, f) => renderWeir(f));
  SWAN_SPR = [SWAN, SWAN_DIP].map(rows => [sprite(rows, SWAN_KEY), sprite(rows, SWAN_KEY, true)]);
  DUCK_SPR = {};
  for (const k in DUCKS) DUCK_SPR[k] = [sprite(DUCKS[k], DUCK_KEY), sprite(DUCKS[k], DUCK_KEY, true)];
  STEAM_C = renderSteamer();
  BAL_C = renderBalustrade(); STREET_C = renderStreet(); BENCH_C = renderBench(); LAMP_C = renderLampPost(); WIRE_C = renderWire();
  TRAM_C = renderTram(); TRAM_F = flipC(TRAM_C);
  BRANCH_L = renderBranch(false); BRANCH_R = renderBranch(true);
  BL_NEAR = Array.from({ length: 16 }, (_, k) => renderBlossomNear(k, 16)); BL_MID = [0, 1, 2, 3].map(renderBlossomMid);
  VIG_C = renderVignette();
  PUFF = [1, 2].map(rr => {
    const s = rr * 2 + 1, p = pix(s, s);
    for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) { const d = Math.hypot(i - rr, y - rr) / (rr + 0.5); if (d < 1 && (1 - d) * 1.4 > bayer(i, y)) p.set(i, y, '#9a9aa8'); }
    return p.done();
  });
  WALKER_SPR = [0, 1].map(fr => sprite(fr
    ? ['.hh..', 'hhs..', '.hs..', '.cc..', 'cccc.', 'cccc.', 'cccc.', 'cccc.', 'cccc.', 'cccc.', '.ppp.', '.p.p.', '.p.p.', 'p...p', 'p...p', 'p...p', 'p...p', 'p...p', 'k...k']
    : ['.hh..', 'hhs..', '.hs..', '.cc..', 'cccc.', 'cccc.', 'cccc.', 'cccc.', 'cccc.', 'cccc.', '.pp..', '.pp..', '.pp..', '.pp..', '.pp..', '.pp..', '.pp..', '.pp..', '.kk..'],
    { h: '#1a1210', s: '#8a6a58', c: '#2a2a34', p: '#141418', k: '#0a0a0c' }));
  buildWx();
}

/* ================= Per-frame: sky, hills, castle, town ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e4e8f8';
  for (const s of STARS) {
    const a = s.b * (0.6 + 0.4 * wave(t, s.k, s.ph));
    ctx.globalAlpha = a; ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha = a * 0.3; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.9);
  ctx.drawImage(MOON_C, MOON.x - 8, MOON.y - 8);
  halo(HL.moonCore, MOON.x - 2, MOON.y, 0.6);
  for (const L of WISPS) { const off = scroll(t, L.k, W); ctx.drawImage(L.c, off - W, L.y); ctx.drawImage(L.c, off, L.y); }
  if (t >= PLANE.t0 && t < PLANE.t1) {                                                    // a plane, very high, blinking across
    const u = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(lerp(-6, W + 6, u)), y = Math.round(lerp(14, 9, u));
    dot(x, y, '#5a5e78');
    if (frac(t * 0.8) < 0.14) { dot(x, y, '#ffffff'); halo(HL.tiny, x, y, 0.6); }
    if (frac(t * 0.8 + 0.5) < 0.3) dot(x - 1, y, '#ff4a3a');
  }
}
function drawHills(t) {
  blit(HILLS);
  halo(HL.petrin, PETRIN.x, 78, 0.85 + 0.1 * wave(t, 6));
  blit(PTOWER);
  const bk = frac(t * 96 / LOOP) < 0.35;                                                  // the red beacon on Petrin's mast
  if (bk) { dot(PETRIN.x, 55, '#ff4a3a'); halo(HL.beacon, PETRIN.x, 55, 0.9); } else dot(PETRIN.x, 55, '#5a1a18');
  for (const l of HILL_LAMPS) halo(HL.tiny, l.x, l.y, 0.55 + 0.25 * wave(t, l.k, l.ph));
  const a = 0.5 * wave(t, 40), tipX = METRO.x + Math.sin(a) * 15, tipY = METRO.y - Math.cos(a) * 15;   // the metronome ticks on Letna
  ctx.fillStyle = '#4a4e68';
  for (let k = 1; k <= 15; k++) ctx.fillRect(Math.round(lerp(METRO.x, tipX, k / 15)), Math.round(lerp(METRO.y, tipY, k / 15)), 1, 1);
  dot(tipX, tipY, '#ff5a44'); halo(HL.beacon, tipX, tipY, 0.7);
}
function soundRing(cx, cy, rr, a) {
  const n = Math.floor(rr * 2.4);
  ctx.globalAlpha = a;
  for (let j = 0; j < n; j++) { const an = j / n * TAU; ctx.fillRect(Math.round(cx + Math.cos(an) * rr), Math.round(cy + Math.sin(an) * rr * 0.8), 1, 1); }
}
function winOn(w, t) { return hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 7) < w.p; }
function drawCastle(t, clock) {
  halo(HL.castle, 232, 104, 0.95); halo(HL.castleCore, 226, 110, 0.8); halo(HL.castleCore, 302, 112, 0.5); halo(HL.vitus, MT.cx, 66, 0.9);
  ctx.drawImage(CASTLE, 0, 0);
  hands(ctx, MT.cx, 51, clock, 180, 1, 1, GOLD[4], GOLD[5], null);             // St. Vitus's clock: Prague's time
  const FX = 184, FY = PAL.roof - 9;                                                       // the flag over the palace, stirring
  box(FX, FY, 1, 9, '#8a7458'); dot(FX, FY - 1, GOLD[3]);
  const fw = step(t, 720) & 1, fw2 = step(t, 720) % 3 === 0 ? 1 : 0;
  box(FX + 1, FY, 3, 1, '#f0ece4'); box(FX + 1, FY + 1, 3, 1, '#d8302c'); dot(FX + 1, FY, '#2a4aa8'); dot(FX + 1, FY + 1, '#2a4aa8');
  box(FX + 4, FY + fw, 1, 1, '#e0dcd4'); box(FX + 4, FY + 1 + fw, 1, 1, '#b82826');
  if (fw2) dot(FX + 3, FY - 1, '#f0ece4');
  ctx.fillStyle = WARMWIN[1];
  for (const w of CASTLE_WINS) if (winOn(w, t)) ctx.fillRect(w.x, w.y, 1, 2);
  let b = 0;
  for (const tb of BELLS) { const dt = t - tb; if (dt >= 0 && dt < 3.4) b = Math.max(b, Math.exp(-dt * 1.3) * (1 - dt / 3.4)); }
  halo(HL.lantern, MT.cx, 36, 0.45 + 0.1 * wave(t, 12) + 0.55 * b);
  if (b > 0.02) {
    ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = '#ffd890';
    for (const tb of BELLS) { const dt = t - tb; if (dt >= 0 && dt < 2.8) { soundRing(MT.cx, 36, 7 + dt * 26, (1 - dt / 2.8) * 0.3); soundRing(MT.cx, 36, 5 + dt * 26, (1 - dt / 2.8) * 0.12); } }
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
}
function drawTown(t) {
  halo(HL.nicholas, SN.cx, 110, 0.8);
  blit(TOWN);
  ctx.fillStyle = WARMWIN[1];
  for (const w of TOWN_WINS) if (winOn(w, t)) ctx.fillRect(w.x, w.y, 1, w.h);
  halo(HL.lantern, SN.cx, 98, 0.5 + 0.08 * wave(t, 10, 0.4));
  for (const l of FAR_LAMPS) halo(HL.tiny, l.x, l.y, l.a);
  halo(HL.ltow, 120, 152, 0.9);
  blit(LTOW);
  ctx.drawImage(FARW_C, 0, FWL + 1);
  FAR_LAMPS.forEach((l, i) => { if (l.y < FWL && (i & 1)) streak(ctx, t, l.x, 1, '#e8b068', 0.22, i, FWL + 2, FWL + 12); });
}

/* ================= Per-frame: blossoms (Prague's own falling kind) ================= */
function drawBlossoms(layer, t) {
  for (const s of BLOSSOMS[layer]) {
    const { cyc, u } = cycle(t, s.n, s.ph);
    const land = layer === 1 ? 0.93 : 1, fu = Math.min(1, u / land), sw = wave(t, s.ks, s.p2) * (layer === 1 ? 1 - smooth(0.84, land, u) : 1);
    const bx = -30 + hash(s.id, cyc, 5) * (W + 50) + Math.min(u, land) * s.drift;
    let yEnd = layer === 0 ? 178 + s.land * 12 : layer === 1 ? 201 + s.land * 26 : H + 12;
    if (layer === 1) yEnd = Math.max(yEnd, wlAt(clamp(Math.round(bx), 0, W - 1)) + 2);
    if (layer === 1 && u >= land) {                                                      // settles on the water: a small ring
      const g = (u - land) / (1 - land), rr = 1 + g * 3;
      ctx.globalAlpha = (1 - g) * 0.45; ctx.fillStyle = '#9aa4c4';
      for (let j = 0; j < 8; j++) { const a = j / 8 * TAU; ctx.fillRect(Math.round(bx + Math.cos(a) * rr), Math.round(yEnd + Math.sin(a) * rr * 0.4), 1, 1); }
      continue;
    }
    const x = bx + sw * s.amp, y = lerp(layer === 0 ? 96 : layer === 1 ? 20 : -12, yEnd, fu) - Math.abs(sw) * (layer + 1) * 1.2;
    if (x < -8 || x > W + 8) continue;
    const spin = frac(s.kspin * t / LOOP + s.p4);
    if (layer === 0) { ctx.globalAlpha = 0.7 * Math.min(1, fu * 8); dot(x, y, BLOSSOM[Math.floor(s.tone * 3)]); continue; }
    const spr = layer === 1 ? BL_MID[Math.floor(spin * 4) % 4] : BL_NEAR[Math.floor(spin * 16) % 16];
    ctx.globalAlpha = layer === 1 ? 0.9 * Math.min(1, fu * 6) : 0.95;
    ctx.drawImage(spr, Math.round(x) - (spr.width >> 1), Math.round(y) - (spr.height >> 1));
  }
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: Charles Bridge, the towers at each end, the terrace ================= */
function drawWalkers(t) {
  for (const p of WALKERS) {
    let s;
    if (p.fixed !== undefined) s = p.fixed;
    else { s = frac(p.k * t / LOOP + p.ph); if (p.dir < 0) s = 1 - s; }
    if (s < 0.03 || s > 0.97) continue;
    const sc = bsc(s), x = Math.round(bxAt(s)), rail = Math.round(bRail(s));
    const hgt = Math.max(2, Math.round(4.4 * sc)), bw = sc > 0.7 ? 2 : 1;
    const bob = p.fixed === undefined && sc > 0.6 ? (Math.floor(t * 3.2 + p.bob * 7) & 1) : 0;
    const one = dx => {
      box(x + dx, rail - hgt + 1 + bob, bw, hgt - 1 - bob, p.col);
      dot(x + dx + (bw > 1 && p.dir > 0 ? 1 : 0), rail - hgt + bob, p.head);
    };
    one(0);
    if (p.pair) one(bw + 1);
  }
}
function drawBridge(t) {
  for (let k = 0; k < 7; k++) { const s = (k + 0.5) / 7; halo(HL.brGlow, bxAt(s), bWater(s) - 10 * bsc(s), 0.8 * (0.5 + 0.5 * bsc(s))); }
  drawWalkers(t);
  blit(BRIDGE_C);
  BR_LAMPS.forEach((l, i) => {
    const I = 0.82 + 0.1 * wave(t, 200 + i * 13, i * 0.37) + (i === 5 && t > 110 && t < 111.6 ? (hash(step(t, 4800), 3) < 0.5 ? -0.6 : 0) : 0);
    halo(HL.brLamp, l.x, l.y, I * (0.55 + 0.45 * l.sc));
  });
  ctx.fillStyle = GOLD[5];                                                               // Nepomuk's stars: a slow sparkle round the ring
  for (const n of NEPO) {
    const v = 0.5 + 0.5 * wave(t, 30, -n.k * 0.2);
    ctx.globalAlpha = 0.3 + 0.7 * v * v; ctx.fillRect(n.x, n.y, 1, 1);
    halo(HL.star, n.x, n.y, 0.3 + 0.5 * v);
  }
  ctx.globalAlpha = 1;
  halo(HL.tower, OT.cx, 150, 0.9);
  blit(OTOWER);
}
function drawLavka(t) {
  blit(LAVKA);
  halo(HL.terrace, 410, 198, 0.9); halo(HL.terrace, 452, 198, 0.85);
  for (const b of TERRACE_BULBS) {
    const I = 0.7 + 0.3 * wave(t, b.k * 7, b.ph);
    ctx.globalAlpha = I; dot(b.x, b.y, '#ffe6a8'); ctx.globalAlpha = 1;
    halo(HL.bulb, b.x, b.y, I * 0.8);
  }
}

/* ================= Per-frame: the river (mirror, streaks, weir), swans, ducks, the steamer ================= */
function drawRiver(t, billboard, blossoms) {
  ctx.drawImage(WATER_C, 0, WY0);
  for (let y = 184; y < WB; y++) {                                                       // the mirrored bridge, rippling
    const k = y - 184;
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 2.3) * (0.4 + k * 0.03) + Math.sin(TAU * 110 * t / LOOP - k * 3.1) * (0.4 + k * 0.02));
    ctx.globalAlpha = 0.72 + 0.28 * Math.sin(TAU * 60 * t / LOOP + k * 1.7);
    ctx.drawImage(REF_C, 0, y - WY0, W, 1, off, y, W, 1);
  }
  ctx.globalAlpha = 1;
  BR_LAMPS.forEach((l, i) => { const wl = wlAt(Math.round(l.x)); streak(ctx, t, l.x, 2 + Math.round(2 * l.sc), '#ffd28a', 0.4 * l.sc + 0.1, i * 1.7, wl, WB); });
  streak(ctx, t, MOON.x, 4, '#d8dcf0', 0.2, 50, wlAt(MOON.x) + 1, WB);                  // the moon's glitter path
  streak(ctx, t, OT.cx - 4, 7, '#f0b060', 0.2, 31, NL.wl + 1, WB);
  streak(ctx, t, 410, 5, '#ffb860', 0.3, 32, NL.wl + 1, WB); streak(ctx, t, 452, 5, '#ffb860', 0.28, 33, NL.wl + 1, WB);
  if (billboard) streak(ctx, t, BB.x + (BB.w >> 1), 9, '#ff9a60', 0.22, 34, wlAt(BB.x + (BB.w >> 1)), WB);
  ctx.drawImage(WEIR_F[step(t, 960) % WEIR_NF], 0, WEIR_TOP);
  for (let y = 186, n = 0; y < WB; y += 2, n++) for (let j = 0; j < 3; j++) {           // drifting troughs and glints
    const k = 2 + ((n * 7 + j * 5) % 7), len = 4 + Math.floor(hash(n, j, 41) * 14);
    const x = Math.round(frac(hash(n, j, 40) + k * t / LOOP) * (W + 40)) - 20;
    if (y <= wlAt(clamp(x, 0, W - 1)) || y <= wlAt(clamp(x + len, 0, W - 1))) continue;
    ctx.globalAlpha = j === 2 ? 0.14 : 0.3;
    box(x, y + (j & 1), len, 1, j === 2 ? '#7a86aa' : '#060a16');
  }
  ctx.globalAlpha = 1;
  if (blossoms) {
    ctx.fillStyle = BLOSSOM[1];
    for (const f of FLOATERS) {
      const x = Math.round(frac(f.x - f.k * t / LOOP) * (W + 20)) - 10, y = f.y + Math.round(wave(t, 20, f.ph) * 0.6);
      if (x < 0 || x >= W || y <= wlAt(x) + 1) continue;
      ctx.globalAlpha = 0.6; ctx.fillRect(x, y, 1, 1);
    }
    ctx.globalAlpha = 1;
  }
}
function swimPos(b, t) {
  const ax = TAU * (b.kx * t / LOOP + b.px);
  return { x: b.cx + b.ax * Math.sin(ax), y: b.cy + b.ay * Math.sin(TAU * (b.ky * t / LOOP + b.py)), vx: Math.cos(ax) };
}
function drawSwans(t) {
  for (const b of DUCKS_AT) {
    const { x, y, vx } = swimPos(b, t), X = Math.round(x), Y = Math.round(y), dir = vx >= 0 ? 0 : 1;
    ctx.globalAlpha = 0.4; box(X - 2, Y + 1, 6, 1, '#2a3258'); ctx.globalAlpha = 1;
    ctx.drawImage(DUCK_SPR[b.kind][dir], X - 2, Y - 3);
  }
  for (const b of SWANS) {
    const { x, y, vx } = swimPos(b, t), X = Math.round(x), Y = Math.round(y), dir = vx >= 0 ? 0 : 1;
    const dip = b.dips.some(([a, e]) => t >= a && t < e), spr = SWAN_SPR[dip ? 1 : 0][dir];
    ctx.globalAlpha = 0.28;                                                               // reflection and a soft wake
    ctx.drawImage(spr, 0, 5, 11, 4, X - 5, Y + 1, 11, 4);
    ctx.fillStyle = '#8a94bc';
    for (let k = 1; k < 7; k++) { const wx = X + (dir ? 5 : -6) + (dir ? k * 2 : -k * 2); ctx.fillRect(wx, Y + 1 + (k >> 1), 1, 1); ctx.fillRect(wx, Y + 1 - (k >> 2), 1, 1); }
    ctx.globalAlpha = 1;
    ctx.drawImage(spr, X - 5, Y - 8);
    if (dip) { ctx.globalAlpha = 0.5; ctx.fillStyle = '#b8c0dc'; ctx.fillRect(X + (dir ? -5 : 4), Y + 1, 2, 1); ctx.globalAlpha = 1; }
  }
}
const ST_WL = 226;                                   // the steamer's waterline
function drawSteamer(t) {
  if (t < STEAMER.t0 || t >= STEAMER.t1) return;
  const u = (t - STEAMER.t0) / (STEAMER.t1 - STEAMER.t0), bx = Math.round(lerp(W + 6, -ST_W - 30, u)), by = ST_WL - ST_H + 1;
  for (let j = 0; j < 14; j++) {                                                         // reflection
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 2.3) * (0.4 + j * 0.06) + Math.sin(TAU * 110 * t / LOOP - j * 3.1) * 0.5);
    ctx.globalAlpha = 0.3 * (1 - j / 14);
    ctx.drawImage(STEAM_C, 0, ST_H - 2 - j, ST_W, 1, bx + off, ST_WL + 1 + j, ST_W, 1);
  }
  const st = step(t, 1440);
  for (let k = 0; k < 30; k++) {                                                         // the wake astern and the bow wave
    if (hash(k, st, 17) > 0.82 - k * 0.014) continue;
    ctx.globalAlpha = 0.5 * (1 - k / 30);
    dot(bx + ST_W - 2 + k * 3, ST_WL + 1 + k * 0.3, k < 4 ? '#c8ccdc' : '#6e78a0');
    if (k > 3) dot(bx + ST_W - 3 + k * 3, ST_WL - k * 0.08, '#5a6490');
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(STEAM_C, bx, by);
  for (let k = 0; k < 9; k++) {                                                          // the paddle wheel churns
    const v = hash(k, st, 23);
    if (v < 0.4) continue;
    ctx.globalAlpha = v; dot(bx + 57 + k * 2 + (st & 1), ST_WL - 1 + (v > 0.8 ? -1 : 0), '#eef2fa');
  }
  ctx.globalAlpha = 1;
  ST_LIGHTS.forEach(([x0, y0, x1, y1], si) => {                                          // strings of bulbs, bow to stern
    const n = Math.round(Math.hypot(x1 - x0, y1 - y0) / 4);
    for (let k = 0; k <= n; k++) {
      const x = bx + Math.round(lerp(x0, x1, k / n)), y = by + Math.round(lerp(y0, y1, k / n) + Math.sin(Math.PI * k / n) * 1.5);
      const on = ((k + si + step(t, 480)) % 4) !== 0;
      dot(x, y, on ? (k & 1 ? '#ffe6a0' : '#fff6e0') : '#6a5030');
      if (on && (k & 1)) halo(HL.stBulb, x, y, 0.8);
    }
  });
  halo(HL.steam, bx + 52, by + 18, 0.9);
  for (let k = 0; k < 7; k++) {                                                          // smoke from the funnel, trailing astern
    const v = frac(t * 30 / LOOP + k / 7), rr = v < 0.35 ? 0 : 1;
    const x = bx + 58 + v * 26 + wave(t, 17, k * 0.2) * v * 2, y = by - 1 - v * 16;
    ctx.globalAlpha = 0.28 * Math.min(1, v * 6) * (1 - v);
    ctx.drawImage(PUFF[rr], Math.round(x) - rr - 1, Math.round(y) - rr - 1);
  }
  ctx.globalAlpha = 1;
  streak(ctx, t, bx + 52, 16, '#ffc070', 0.22, 12, ST_WL + 1, WB);
}

/* ================= Per-frame: the embankment (couple, walkers, tram, lamp, branches) ================= */
function drawWalkersFg(t) {
  for (const w of WALKERS_FG) {
    if (t < w.a || t >= w.b) continue;
    const u = (t - w.a) / (w.b - w.a), X = Math.round(w.dir > 0 ? lerp(-20, W + 14, u) : lerp(W + 14, -20, u)), fr = Math.floor(t * 4) & 1;
    const spr = WALKER_SPR[fr];
    ctx.save();
    if (w.dir < 0) { ctx.translate(X + 4, 0); ctx.scale(-1, 1); ctx.drawImage(spr, 0, SIDE - 18); } else ctx.drawImage(spr, X, SIDE - 18);
    ctx.restore();
    ctx.globalAlpha = 0.85; box(X, SIDE - 14, 4, 6, w.coat); ctx.globalAlpha = 1;
    if (w.dog) {                                                                        // a little dog trotting ahead on its lead
      const dx = X + (w.dir > 0 ? 9 : -8), leg = fr;
      box(dx, SIDE - 3, 4, 2, '#3a2a20'); dot(dx + (w.dir > 0 ? 4 : -1), SIDE - 4, '#3a2a20'); dot(dx + (w.dir > 0 ? 4 : -1), SIDE - 3, '#3a2a20');
      dot(dx + leg, SIDE - 1, '#2a1e18'); dot(dx + 3 - leg, SIDE - 1, '#2a1e18');
      ctx.globalAlpha = 0.6; for (let k = 1; k < 6; k++) dot(lerp(X + (w.dir > 0 ? 3 : 0), dx + (w.dir > 0 ? 0 : 3), k / 6), SIDE - 11 + k * 1.4, '#1a1a1e'); ctx.globalAlpha = 1;
    }
  }
}
function drawTrams(t) {
  for (const tr of TRAMS) {
    if (t < tr.a || t >= tr.b) continue;
    const u = (t - tr.a) / (tr.b - tr.a), X = Math.round(tr.dir > 0 ? lerp(-TRAM.w - 12, W + 12, u) : lerp(W + 12, -TRAM.w - 12, u));
    const Y = TRAM.y + 12;
    halo(HL.tramGlow, X + TRAM.w * 0.3, 246, 0.8); halo(HL.tramGlow, X + TRAM.w * 0.7, 246, 0.8);
    ctx.drawImage(tr.dir > 0 ? TRAM_C : TRAM_F, X, Y);
    const px0 = X + (TRAM.w >> 1), wy = wireY(clamp(px0, 0, WIRE_X1 - 1)) + 1, mid = Math.round((wy + Y) / 2);   // the diamond pantograph
    ctx.fillStyle = '#6a6a78';
    for (let k = 0; k <= 4; k++) {
      const yl = Math.round(lerp(Y - 1, mid, k / 4)), yu = Math.round(lerp(mid, wy, k / 4));
      ctx.fillRect(px0 - k, yl, 1, 1); ctx.fillRect(px0 + k, yl, 1, 1);
      ctx.fillRect(px0 - 4 + k, yu, 1, 1); ctx.fillRect(px0 + 4 - k, yu, 1, 1);
    }
    ctx.fillStyle = '#a8a8b4'; ctx.fillRect(px0 - 3, wy, 7, 1);
    ctx.fillStyle = '#3a3a44'; ctx.fillRect(px0 - 6, Y - 1, 13, 1); ctx.fillRect(px0 + 16, Y - 2, 12, 2); ctx.fillRect(px0 - 30, Y - 2, 10, 2);   // roof gear
    const front = tr.dir > 0 ? X + TRAM.w - 2 : X + 1, back = tr.dir > 0 ? X + 1 : X + TRAM.w - 2;
    halo(HL.head, front, Y + 19, 1); halo(HL.tail, back, Y + 19, 0.8);
    const sp = hash(tr.seed, step(t, SPARKS), 7);                                        // the pantograph sparks on the wire
    if (sp < 0.04) {
      const I = 0.6 + sp * 10;
      halo(HL.spark, px0, wy, I);
      ctx.fillStyle = '#f4faff'; ctx.fillRect(px0, wy - 1, 1, 3); ctx.fillRect(px0 - 1, wy, 3, 1);
      ctx.globalAlpha = 0.6; ctx.fillStyle = '#9ac8ff'; ctx.fillRect(px0 - 6, wy - 1, 13, 1); ctx.globalAlpha = 1;
    }
  }
}
const LAMP_FLICK = [[121.4, 122.8], [230.2, 230.9]];
function drawLamp(t) {
  let I = 0.93 + 0.05 * wave(t, 900, 0.2);
  for (const [a, b] of LAMP_FLICK) if (t >= a && t < b) I = hash(step(t, 4800), 29) < 0.4 ? 0.3 : 0.9;
  blit(LAMP_C);
  if (I < 0.8) { ctx.globalAlpha = 0.7 * (1 - I); box(LP.x - 4, LP.y - 5, 9, 17, '#2a1a10'); ctx.globalAlpha = 1; }
  halo(HL.lamp, LP.x, LP.y + 2, I); halo(HL.lampCore, LP.x, LP.y + 1, I * 0.9);
  ctx.fillStyle = '#f0e8d0';                                                              // moths around the lantern
  for (const [k1, k2, ph, r1, r2] of [[37, 29, 0.1, 10, 7], [41, 47, 0.5, 8, 9], [53, 59, 0.8, 12, 6]]) {
    const x = LP.x + r1 * Math.sin(TAU * (k1 * t / LOOP + ph)), y = LP.y + 1 + r2 * Math.sin(TAU * (k2 * t / LOOP + ph * 2));
    ctx.globalAlpha = 0.75; ctx.fillRect(Math.round(x), Math.round(y), 1, 1);
  }
  ctx.globalAlpha = 1;
}
/** a linden branch bobbing in the breeze: the free tips move more than the end held by the tree */
function drawBranch(o, t, side) {
  const w = o.c.width, h = o.c.height, sw = 1.3 * wave(t, 12, side ? 0.2 : 0.7) + 0.6 * wave(t, 29, side ? 0.5 : 0.1);
  const offAt = i => Math.round(sw * clamp((side ? W - (o.x + i) : o.x + i) / 140, 0, 1));
  let i = 0;
  while (i < w) {
    const off = offAt(i);
    let i2 = i + 1;
    while (i2 < w && offAt(i2) === off) i2++;
    ctx.drawImage(o.c, i, 0, i2 - i, h, o.x + i, o.y + off, i2 - i, h);
    i = i2;
  }
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the sign; billboard: false leaves the sign out (the loop videos);
// weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out
function renderFrame(t, { pitch, billboard = true, weather, clock, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  drawSky(t);
  drawHills(t);
  drawCastle(t, clock);
  drawTown(t);
  if (billboard) drawBoard(t, pitch);
  if (wx.blossoms) drawBlossoms(0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc[0], reducedMotion);
  drawBridge(t);
  drawLavka(t);
  drawRiver(t, billboard, wx.blossoms);
  drawSwans(t);
  drawSteamer(t);
  if (wx.blossoms) drawBlossoms(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc[1], reducedMotion);
  ctx.drawImage(STREET_C, 0, 0);
  blit(BAL_C);
  blit(BENCH_C);
  drawCouple(t);
  drawWalkersFg(t);
  drawTrams(t);
  blit(WIRE_C);
  drawLamp(t);
  drawBranch(BRANCH_L, t, false);
  drawBranch(BRANCH_R, t, true);
  if (wx.blossoms) drawBlossoms(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc[2], reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'prague',
  name: 'Prague',
  tagline: 'Lamps on Charles Bridge',
  timeZone: 'Europe/Prague',
  accent: '#7ddc82',
  focusX: 248,
  landmark: [200, 270],                               // Prague Castle's St. Vitus Cathedral, west spires to choir
  landmarkTop: 19,                                    // the row of the main tower's cross, which landscape crops keep
  billboard: BB,
  ambience: { water: 0.45, wind: 0.12, crowd: 0.14, traffic: 0.05 },
  events: [
    ...BELLS.map(t => ({ t, type: 'bell', x: MT.cx, gain: 0.55 })),
    ...TRAMS.flatMap(tr => [
      { t: tr.a + 0.4, type: 'chime', x: tr.dir > 0 ? 40 : 440, gain: 0.35 },
      { t: tr.a, type: 'train', dur: tr.b - tr.a, pan: tr.dir > 0 ? [-0.9, 0.9] : [0.9, -0.9], gain: 0.5 },
    ]),
    { t: STEAMER.t0 + 1, type: 'horn', x: 460, gain: 0.5 },
    { t: STEAMER.t0, type: 'boat', dur: STEAMER.t1 - STEAMER.t0, pan: [0.9, -0.9], gain: 0.3 },
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
